import { useState, useMemo } from 'react';
import { useLang } from '../../config/i18n';
import { TODAY, MONTHS, CURRENT_MONTH_KEY } from '../../config/constants';
import { SEED_CASH } from '../../data/seed';
import { Modal, Field, SaveBtn, CostPill } from '../shared/UI';
import { generatePaymentDates } from '../../lib/helpers';

// ─────────────────────────────────────────────────────────────────────
// Overhead / G&A helpers — used across all Finance sub-tabs so recurring
// SaaS/government/legal entries roll into P&L on the correct months.
// ─────────────────────────────────────────────────────────────────────

// Does a recurring overhead entry hit a given month (mKey in "YYYY-MM" format)?
// Handles one-off, monthly, quarterly, semi-annual, annual frequencies, plus
// an optional endDate to stop recurrence.
function overheadHitsMonth(entry, mKey){
  if (!entry || !entry.startDate) return false;
  const startMonth = String(entry.startDate).slice(0, 7);
  const endMonth = entry.endDate ? String(entry.endDate).slice(0, 7) : null;
  if (mKey < startMonth) return false;
  if (endMonth && mKey > endMonth) return false;
  const freq = entry.freq || 'once';
  if (freq === 'once')    return mKey === startMonth;
  if (freq === 'monthly') return true;
  const [sY, sM] = startMonth.split('-').map(Number);
  const [mY, mM] = mKey.split('-').map(Number);
  const diff = (mY - sY) * 12 + (mM - sM);
  if (freq === 'quarterly')   return diff >= 0 && diff % 3 === 0;
  if (freq === 'semi-annual') return diff >= 0 && diff % 6 === 0;
  if (freq === 'annual')      return diff >= 0 && diff % 12 === 0;
  return false;
}

function overheadTotalForMonth(overhead, mKey){
  return (overhead || []).reduce((s, e) => overheadHitsMonth(e, mKey) ? s + (Number(e.amount) || 0) : s, 0);
}

const OVERHEAD_CATEGORIES = [
  { key: 'saas',       label: '💻 SaaS & Software',  color: '#38bdf8' },
  { key: 'government', label: '🏛️ حكومي',            color: '#a78bfa' },
  { key: 'legal',      label: '⚖️ Legal & Admin',    color: '#c8a03c' },
  { key: 'marketing',  label: '📢 Marketing',        color: '#22c55e' },
  { key: 'other',      label: '📦 Other',            color: '#94a3b8' },
];

const OVERHEAD_FREQ = [
  { key: 'once',        label: 'مرة واحدة' },
  { key: 'monthly',     label: 'شهري' },
  { key: 'quarterly',   label: 'ربع سنوي' },
  { key: 'semi-annual', label: 'نصف سنوي' },
  { key: 'annual',      label: 'سنوي' },
];

function ProfitabilityTab({ contracts = [], setContracts = ()=>{}, invoices = [], overhead = [], leases, setLeases, monthly }){
  const {lang} = useLang();
  const bl = (ar, en) => lang === "en" ? en : ar;
  const [editing,   setEditing]  = useState(null);
  const [form,      setForm]     = useState({});
  const [viewMonth, setViewMonth]= useState(CURRENT_MONTH_KEY);
  const [sortBy,    setSortBy]   = useState("pnl"); // pnl | cost | rev

  // ── Data helpers ──────────────────────────────────────────────
  // Each "row" is a master contract. The contract is the source of truth for the primary
  // lease terms (annualRent + officeFee + waterAnnual + ejarNum + dates). `leases[contract.id]`
  // stores supplementary Finance-tab-only fields (otherMonthly extras, setupFee, assumedRent,
  // notes). This means when the contract is renewed in the Contracts tab, its rent/cost/Ejar
  // automatically flow through here without any manual re-entry.
  function getContract(cid){ return contracts.find(c => c.id === cid) || {}; }
  function getLease(cid){ return leases[cid] || {otherMonthly:0,setupFee:0,assumedRent:0,notes:""}; }

  // Annual landlord-paid lease cost = contract.annualRent + officeFee + waterAnnual.
  function contractAnnualCost(cid){
    const c = getContract(cid);
    return (Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0);
  }

  // Historical accuracy: if the monthly record has a frozen snapshot (written when ops
  // recorded that month), use it. Otherwise fall back to the live contract cost. This means
  // renewing a contract with a new rent won't rewrite past months' lease cost.
  function frozenAnnualCost(cid, mKey){
    const op = mKey ? (monthly[`${cid}-${mKey}`]||{}) : {};
    if (op.leaseCostAtRecord != null) return Number(op.leaseCostAtRecord)||0;
    return contractAnnualCost(cid);
  }

  // Monthly = amortised contract cost (frozen when possible) + any Finance-tab extras.
  function monthlyLeaseCost(cid, mKey){
    const l = getLease(cid);
    return Math.round(frozenAnnualCost(cid, mKey)/12) + (Number(l.otherMonthly)||0);
  }

  // Single source of truth: a contract id's monthly record. No sibling fallback.
  // Data migration moves archived-sibling entries to the current contract id, so one key wins per month.
  function getMonthOp(cid, mKey){ return monthly[`${cid}-${mKey}`]||{}; }

  // Revenue is recognised in the month an invoice is paid. Match invoices to this contract
  // by contractId (preferred), ejarNum, or unit+district (legacy). Fall back to the older
  // monthly.rentCollected field so pre-invoice-driven months don't disappear.
  function getRevenue(cid, mKey){
    const c = getContract(cid);
    const cu = String(c.unitNumber || c.unitName || '').toLowerCase();
    const cd = String(c.district || '').toLowerCase();
    const invoiceRev = (invoices||[]).reduce((s, inv) => {
      if (!inv.paidAt || !String(inv.paidAt).startsWith(mKey)) return s;
      const matches =
        (inv.contractId && inv.contractId === cid) ||
        (inv.ejarNum && c.ejarNum && String(inv.ejarNum).trim() === String(c.ejarNum).trim()) ||
        (!inv.contractId && !inv.ejarNum &&
          String(inv.unit||'').toLowerCase() === cu &&
          (!inv.district || String(inv.district||'').toLowerCase() === cd));
      return matches ? s + (Number(inv.rent)||0) : s;
    }, 0);
    // Fallback: manually recorded revenue in Monthly Ops (legacy or override)
    return invoiceRev > 0 ? invoiceRev : (Number(getMonthOp(cid,mKey).rentCollected)||0);
  }

  // Variable monthly costs recorded in the Monthly Ops tab:
  //   - electricityCost  — dedicated slot (utility bill per unit)
  //   - maintenanceCost  — auto-computed sum of the "Other Expenses" flexible list
  //                        (internet, cleaning, repairs, anything the ops team enters)
  function getElectricity(uid, mKey){ return Number(getMonthOp(uid,mKey).electricityCost)||0; }
  function getMaintenanceCost(uid, mKey){ return Number(getMonthOp(uid,mKey).maintenanceCost)||0; }
  function getVariableCost(uid, mKey){ return getElectricity(uid,mKey) + getMaintenanceCost(uid,mKey); }

  function totalMonthlyCost(uid, mKey){
    return monthlyLeaseCost(uid, mKey) + getVariableCost(uid,mKey);
  }

  function monthlyPnL(uid, mKey){ return getRevenue(uid,mKey) - totalMonthlyCost(uid,mKey); }

  function annualRevenue(uid){ return MONTHS.reduce((s,m)=>s+getRevenue(uid,m.key),0); }

  function annualMaintenance(uid){ return MONTHS.reduce((s,m)=>s+getMaintenanceCost(uid,m.key),0); }
  function annualElectricity(uid){ return MONTHS.reduce((s,m)=>s+getElectricity(uid,m.key),0); }
  function annualVariable(uid){ return annualMaintenance(uid) + annualElectricity(uid); }

  // Annual total = sum of monthly lease (frozen when possible) + electricity + other expenses.
  // Summing per-month lets frozen snapshots on past months carry through, so a mid-year rent
  // change doesn't retro-apply.
  function annualTotalCost(cid){
    const annualLease = MONTHS.reduce((s, m) => s + monthlyLeaseCost(cid, m.key), 0);
    return annualLease + annualVariable(cid);
  }

  function annualPnL(cid){ return annualRevenue(cid) - annualTotalCost(cid); }

  // Breakeven occupancy rate = monthly lease cost / assumed rent. Assumed rent falls back to
  // the contract itself if the ops team hasn't overridden it in the Finance edit modal.
  function breakevenRate(cid){
    const l = getLease(cid);
    const c = getContract(cid);
    const rent = Number(l.assumedRent) || Number(c.rent) || 0;
    if(!rent) return null;
    return Math.round((monthlyLeaseCost(cid)/rent)*100);
  }

  // ── Portfolio summary ──────────────────────────────────────────
  const summary = useMemo(()=>{
    // "Active for viewMonth" = contract-date window covers this month (viewMonth-aware).
    // So viewing Oct 2025 still counts contracts that ended since; viewing current month
    // naturally drops expired ones. Archived contracts kept out unless they overlap.
    const monthStart = viewMonth + '-01';
    const monthEnd   = viewMonth + '-31';
    const wasActiveInMonth = c =>
      c.type !== 'storage' &&
      (Number(c.annualRent)||0) > 0 &&
      (!c.contractStart || c.contractStart <= monthEnd) &&
      (!c.contractEnd   || c.contractEnd   >= monthStart);
    const activeContracts = contracts.filter(wasActiveInMonth);
    // Separately: today-valid contracts (for yearly roll-up KPIs).
    const todayISO = TODAY.toISOString().slice(0,10);
    const todayValid = c => c.status !== 'archived' && c.type !== 'storage' && (Number(c.annualRent)||0) > 0
      && (!c.contractEnd || c.contractEnd >= todayISO);
    const liveContracts = contracts.filter(todayValid);
    const totalMonthLease  = activeContracts.reduce((s,c)=>s+monthlyLeaseCost(c.id, viewMonth),0);
    const totalMonthElec   = activeContracts.reduce((s,c)=>s+getElectricity(c.id,viewMonth),0);
    const totalMonthMaint  = activeContracts.reduce((s,c)=>s+getMaintenanceCost(c.id,viewMonth),0);
    const totalMonthOverhead = overheadTotalForMonth(overhead, viewMonth);
    const totalMonthCost   = totalMonthLease + totalMonthElec + totalMonthMaint + totalMonthOverhead;
    const totalMonthRev    = activeContracts.reduce((s,c)=>s+getRevenue(c.id,viewMonth),0);
    const totalMonthPnL    = totalMonthRev - totalMonthCost;
    const totalAnnualCost  = liveContracts.reduce((s,c)=>s+annualTotalCost(c.id),0);
    const totalAnnualRev   = liveContracts.reduce((s,c)=>s+annualRevenue(c.id),0);
    const totalAnnualPnL   = totalAnnualRev - totalAnnualCost;
    const covPct           = totalMonthCost>0 ? Math.round((totalMonthRev/totalMonthCost)*100) : 0;
    // Breakeven = Fixed (lease: rent + office + water) + Variable (electricity + maintenance + G&A overhead).
    // Revenue must cover this to break even for the month.
    const fixedLease = Math.round(activeContracts.reduce((s,c) => s +
      ((Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0)) / 12, 0));
    const breakeven = fixedLease + totalMonthElec + totalMonthMaint + totalMonthOverhead;
    const monthRevVsBreakeven = totalMonthRev - breakeven;
    return{activeUnits:activeContracts,liveContracts,totalMonthLease,totalMonthElec,totalMonthMaint,totalMonthOverhead,totalMonthCost,totalMonthRev,totalMonthPnL,totalAnnualCost,totalAnnualRev,totalAnnualPnL,covPct,breakeven,monthRevVsBreakeven};
  },[contracts,invoices,overhead,leases,monthly,viewMonth]);

  // ── Sorted rows (one per contract) ──────────────────────────────
  const sortedUnits = useMemo(()=>{
    return [...contracts].sort((a,b)=>{
      if(sortBy==="pnl")  return monthlyPnL(b.id,viewMonth)-monthlyPnL(a.id,viewMonth);
      if(sortBy==="cost") return totalMonthlyCost(b.id,viewMonth)-totalMonthlyCost(a.id,viewMonth);
      if(sortBy==="rev")  return getRevenue(b.id,viewMonth)-getRevenue(a.id,viewMonth);
      return 0;
    });
  },[contracts,invoices,leases,monthly,viewMonth,sortBy]);

  function openEdit(c){
    const l = getLease(c.id);
    setForm({
      unitId:       c.id,                                // still called unitId in the form for modal compat
      otherMonthly: String(l.otherMonthly || ""),
      setupFee:     String(l.setupFee || ""),
      assumedRent:  String(l.assumedRent || c.rent || ""),
      notes:        l.notes || "",
    });
    setEditing(c.id);
  }
  function save(){
    // Only supplementary fields are stored here. Primary lease terms (annualRent, officeFee,
    // waterAnnual, dates, Ejar) live on the master contract and are edited from that tab.
    setLeases(p=>({...p,[form.unitId]:{
      otherMonthly: Number(form.otherMonthly)||0,
      setupFee:     Number(form.setupFee)||0,
      assumedRent:  Number(form.assumedRent)||0,
      notes:        form.notes,
    }}));
    setEditing(null);
  }

  const pc = (v)=>v>0?"#22c55e":v<0?"#ef4444":"#64748b";
  const pb = (v)=>v>0?"rgba(34,197,94,0.09)":v<0?"rgba(239,68,68,0.09)":"rgba(255,255,255,0.02)";
  const pbd= (v)=>v>0?"rgba(34,197,94,0.22)":v<0?"rgba(239,68,68,0.22)":"rgba(255,255,255,0.07)";
  const sar= (n)=>n===0?"—":Number(n).toLocaleString()+" ر";

  const sBtn=(val,lbl)=>(
    <button onClick={()=>setSortBy(val)} style={{padding:"5px 13px",borderRadius:8,border:"1px solid",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
      background:sortBy===val?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.03)",
      color:sortBy===val?"#c8a03c":"#64748b",borderColor:sortBy===val?"rgba(200,160,60,0.4)":"transparent"}}>
      {lbl}
    </button>
  );

  // ── Historical audit: orphan contracts ─────────────────────────────
  // Monthly records and invoices can reference contract IDs that no longer exist (because the
  // contract was hard-deleted before archive existed). Their revenue/cost history is intact in
  // Supabase but invisible to P&L because the lookup finds no contract. The banner below surfaces
  // them so Ibrahim can restore the shell contract (same original ID, status='archived') and
  // reconnect the history.
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreForm,   setRestoreForm]   = useState({});
  const orphans = useMemo(() => {
    const contractIds = new Set((contracts||[]).map(c => c.id));
    const buckets = {};
    // Scan monthly records keyed "cXXXX-YYYY-MM" or "sXXXX-YYYY-MM"
    Object.entries(monthly||{}).forEach(([k, data]) => {
      const m = k.match(/^(c\d+|s\d+)-(\d{4}-\d{2})$/);
      if (!m) return;
      const cid = m[1]; const mKey = m[2];
      if (contractIds.has(cid)) return;
      if (!buckets[cid]) buckets[cid] = { contractId: cid, months: [], tenantNames: new Set(), revenue: 0, expenses: 0, invoices: [] };
      buckets[cid].months.push(mKey);
      buckets[cid].revenue  += Number(data?.rentCollected)||0;
      buckets[cid].expenses += (Number(data?.electricityCost)||0) + (Number(data?.maintenanceCost)||0);
      if (data?.tenantName) buckets[cid].tenantNames.add(data.tenantName);
    });
    // Scan invoices for orphan contractId
    (invoices||[]).forEach(inv => {
      if (!inv.contractId || contractIds.has(inv.contractId)) return;
      const cid = inv.contractId;
      if (!buckets[cid]) buckets[cid] = { contractId: cid, months: [], tenantNames: new Set(), revenue: 0, expenses: 0, invoices: [] };
      buckets[cid].invoices.push(inv);
      if (inv.customerName) buckets[cid].tenantNames.add(inv.customerName);
    });
    return Object.values(buckets).map(b => ({
      ...b,
      months: Array.from(new Set(b.months)).sort(),
      tenantNames: Array.from(b.tenantNames),
    }));
  }, [contracts, invoices, monthly]);

  function openRestore(orphan){
    // Prefill from any invoice evidence we found, else from monthly bucket spans
    const firstInv = orphan.invoices[0] || {};
    const firstMonth = orphan.months[0];
    const lastMonth  = orphan.months[orphan.months.length - 1];
    const toDate = mk => mk ? `${mk}-01` : '';
    setRestoreForm({
      unitNumber:     firstInv.unit     || '',
      district:       firstInv.district || '',
      city:           firstInv.city     || 'الرياض',
      ejarNum:        firstInv.ejarNum  || '',
      annualRent:     '',
      officeFee:      '',
      waterAnnual:    '',
      contractStart:  toDate(firstMonth),
      contractEnd:    firstInv.contractEnd || (lastMonth ? toDate(lastMonth) : ''),
      landlord:       '',
      notes:          `استرجاع تلقائي من ${orphan.months.length} سجل شهري` + (orphan.tenantNames.length ? ` · آخر مستأجر: ${orphan.tenantNames[orphan.tenantNames.length-1]}` : ''),
    });
    setRestoreTarget(orphan);
  }
  function saveRestore(){
    if (!restoreTarget) return;
    const f = restoreForm;
    const unitNumber = String(f.unitNumber||'').trim();
    const district   = String(f.district||'').trim();
    const unitName   = unitNumber && district ? `${unitNumber} - ${district}` : (unitNumber || district || `مستعاد ${restoreTarget.contractId}`);
    const recovered = {
      id:            restoreTarget.contractId,   // Same original ID so monthly/invoices re-link
      type:          'unit',
      status:        'archived',                 // Starts archived — this is historical data
      archivedAt:    new Date().toISOString().slice(0,10),
      unitNumber,
      district,
      city:          f.city || 'الرياض',
      unitName,
      ejarNum:       f.ejarNum || '',
      landlord:      f.landlord || '',
      annualRent:    Number(f.annualRent)  || 0,
      officeFee:     Number(f.officeFee)   || 0,
      waterAnnual:   Number(f.waterAnnual) || 0,
      contractStart: f.contractStart || '',
      contractEnd:   f.contractEnd || '',
      notes:         f.notes || '',
      restoredAt:    new Date().toISOString(),
    };
    setContracts(prev => [...(prev||[]), recovered]);
    setRestoreTarget(null);
    setRestoreForm({});
  }

  return(
    <div style={{padding:"24px 28px",maxWidth:1400,margin:"0 auto"}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:22,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontSize:21,fontWeight:800,color:"#c8a03c",margin:0}}>📊 {bl("قائمة الدخل","Income Statement")}</h2>
          <p style={{fontSize:13,color:"#475569",margin:"5px 0 0"}}>الإيرادات · تكاليف عقود الإيجار · الصيانة · صافي الربح والخسارة لكل وحدة</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <select value={viewMonth} onChange={e=>setViewMonth(e.target.value)}
            style={{padding:"7px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)",background:"#181b27",color:"#94a3b8",fontSize:13,outline:"none",fontFamily:"'Tajawal',sans-serif",direction:"rtl"}}>
            {MONTHS.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Historical Audit Banner — only renders when orphaned contract data is found ── */}
      {orphans.length > 0 && (
        <div style={{background:"rgba(249,115,22,0.08)",border:"1px solid rgba(249,115,22,0.35)",borderRadius:14,padding:"16px 20px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
            <span style={{fontSize:18}}>🔧</span>
            <span style={{fontSize:14,fontWeight:800,color:"#f97316"}}>تدقيق تاريخي — {orphans.length} عقد محذوف لديه سجلات مالية</span>
            <span style={{fontSize:11,color:"#94a3b8"}}>الإيرادات والمصاريف محفوظة ولكن مفصولة عن العقد. اضغط "استعادة" لإعادة ربطها كعقد مؤرشف.</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
            {orphans.map(o => (
              <div key={o.contractId} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(249,115,22,0.2)",borderRadius:10,padding:"10px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#e2e8f0"}}>
                      {o.invoices[0]?.unit ? `وحدة ${o.invoices[0].unit}` : 'عقد محذوف'}
                      {o.invoices[0]?.district && <span style={{color:"#94a3b8"}}> · {o.invoices[0].district}</span>}
                    </div>
                    <div style={{fontSize:10,color:"#64748b",marginTop:2,direction:"ltr",textAlign:"right"}}>ID: {o.contractId}</div>
                  </div>
                  <button onClick={()=>openRestore(o)}
                    style={{padding:"5px 12px",borderRadius:7,background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.3)",color:"#22c55e",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>
                    🔄 استعادة
                  </button>
                </div>
                <div style={{display:"flex",gap:6,fontSize:10,color:"#94a3b8",flexWrap:"wrap",marginTop:6}}>
                  {o.months.length > 0 && <span>📅 {o.months.length} شهر ({o.months[0]} → {o.months[o.months.length-1]})</span>}
                  {o.revenue > 0 && <span style={{color:"#22c55e"}}>💰 {o.revenue.toLocaleString()} ر</span>}
                  {o.expenses > 0 && <span style={{color:"#f97316"}}>💸 {o.expenses.toLocaleString()} ر</span>}
                  {o.invoices.length > 0 && <span style={{color:"#38bdf8"}}>📄 {o.invoices.length} فاتورة</span>}
                </div>
                {o.tenantNames.length > 0 && (
                  <div style={{fontSize:10,color:"#64748b",marginTop:4}}>مستأجر: {o.tenantNames.join(' · ')}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Restore Modal — prefilled from the orphan evidence; user confirms + saves ── */}
      {restoreTarget && (
        <Modal title={`🔄 استعادة عقد — ${restoreTarget.contractId}`} onClose={()=>{setRestoreTarget(null);setRestoreForm({});}}>
          <div style={{background:"rgba(249,115,22,0.07)",border:"1px solid rgba(249,115,22,0.22)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#94a3b8",lineHeight:1.6}}>
            <strong style={{color:"#f97316"}}>الأدلة المتوفرة:</strong>
            <div style={{marginTop:4}}>{restoreTarget.months.length} سجل شهري · {restoreTarget.invoices.length} فاتورة · {restoreTarget.tenantNames.length} مستأجر</div>
            {restoreTarget.revenue > 0 && <div style={{color:"#22c55e"}}>إيراد مسجل: {restoreTarget.revenue.toLocaleString()} ريال</div>}
            {restoreTarget.months.length > 0 && <div>الفترة: {restoreTarget.months[0]} → {restoreTarget.months[restoreTarget.months.length-1]}</div>}
            <div style={{marginTop:6,fontSize:11,color:"#64748b"}}>العقد سيُحفظ كمؤرشف — ولن يظهر في اللوحة الحالية، لكن سيُعاد ربط كل البيانات التاريخية.</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="رقم الوحدة" value={restoreForm.unitNumber||''} onChange={v=>setRestoreForm(f=>({...f,unitNumber:v}))} required/>
            <Field label="الحي" value={restoreForm.district||''} onChange={v=>setRestoreForm(f=>({...f,district:v}))} required/>
            <Field label="المدينة" value={restoreForm.city||''} onChange={v=>setRestoreForm(f=>({...f,city:v}))}/>
            <Field label="رقم إيجار" value={restoreForm.ejarNum||''} onChange={v=>setRestoreForm(f=>({...f,ejarNum:v}))}/>
            <Field label="بداية العقد" value={restoreForm.contractStart||''} onChange={v=>setRestoreForm(f=>({...f,contractStart:v}))} type="date"/>
            <Field label="نهاية العقد" value={restoreForm.contractEnd||''} onChange={v=>setRestoreForm(f=>({...f,contractEnd:v}))} type="date"/>
            <Field label="الإيجار السنوي (ريال)" value={restoreForm.annualRent||''} onChange={v=>setRestoreForm(f=>({...f,annualRent:v}))} type="number"/>
            <Field label="رسوم المكتب (ريال)" value={restoreForm.officeFee||''} onChange={v=>setRestoreForm(f=>({...f,officeFee:v}))} type="number"/>
            <Field label="مياه سنوي (ريال)" value={restoreForm.waterAnnual||''} onChange={v=>setRestoreForm(f=>({...f,waterAnnual:v}))} type="number"/>
            <Field label="اسم المالك" value={restoreForm.landlord||''} onChange={v=>setRestoreForm(f=>({...f,landlord:v}))}/>
          </div>
          <Field label="ملاحظات" value={restoreForm.notes||''} onChange={v=>setRestoreForm(f=>({...f,notes:v}))}/>
          <button onClick={saveRestore} disabled={!restoreForm.unitNumber || !restoreForm.district}
            style={{width:"100%",padding:12,borderRadius:10,marginTop:10,
              background:(restoreForm.unitNumber && restoreForm.district) ? "linear-gradient(135deg,#22c55e,#16a34a)" : "rgba(255,255,255,0.05)",
              border:"none",color:(restoreForm.unitNumber && restoreForm.district) ? "#0d0f1a" : "#334155",
              fontSize:14,fontWeight:800,cursor:(restoreForm.unitNumber && restoreForm.district) ? "pointer" : "not-allowed",fontFamily:"'Tajawal',sans-serif"}}>
            🔄 استعادة كعقد مؤرشف
          </button>
        </Modal>
      )}

      {/* ── KPI Cards ── */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {[
          {label:"إجمالي تكلفة الإيجار الشهري",value:summary.totalMonthLease.toLocaleString()+" ريال",accent:"#ef4444",icon:"🏠"},
          {label:"فاتورة الكهرباء",              value:summary.totalMonthElec.toLocaleString()+" ريال", accent:"#fbbf24",icon:"⚡"},
          {label:"المصاريف الأخرى",              value:summary.totalMonthMaint.toLocaleString()+" ريال",accent:"#f97316",icon:"💸"},
          {label:"مصاريف إدارية (G&A)",          value:summary.totalMonthOverhead.toLocaleString()+" ريال",accent:"#a78bfa",icon:"🏢"},
          {label:"إجمالي المصاريف الشهرية",       value:summary.totalMonthCost.toLocaleString()+" ريال", accent:"#f59e0b",icon:"💵"},
          {label:"إيراد الشهر المسجّل",           value:summary.totalMonthRev.toLocaleString()+" ريال",  accent:"#22c55e",icon:"💰"},
          {label:"صافي الشهر (ربح/خسارة)",        value:(summary.totalMonthPnL>0?"+":"")+summary.totalMonthPnL.toLocaleString()+" ريال",accent:pc(summary.totalMonthPnL),icon:summary.totalMonthPnL>=0?"▲":"▼"},
          {label:"نقطة التعادل (محسوبة)",         value:summary.breakeven.toLocaleString()+" ريال", accent:"#a78bfa",icon:"⚖️"},
          {label:"إيراد الشهر مقابل التعادل",      value:(summary.monthRevVsBreakeven>=0?"+":"")+summary.monthRevVsBreakeven.toLocaleString()+" ريال",accent:pc(summary.monthRevVsBreakeven),icon:summary.monthRevVsBreakeven>=0?"✅":"⚠️"},
        ].map((c,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"14px 16px",flex:1,minWidth:135,position:"relative",overflow:"hidden",textAlign:"right"}}>
            <div style={{position:"absolute",top:-5,left:-5,fontSize:36,opacity:0.06}}>{c.icon}</div>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>{c.label}</div>
            <div style={{fontSize:16,fontWeight:800,color:c.accent,lineHeight:1}}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Coverage bar ── */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"14px 20px",marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
          <div style={{fontSize:13,fontWeight:700,color:"#94a3b8"}}>
            {bl("مستوى تغطية المصاريف الشهرية","Monthly Cost Coverage")} — {MONTHS.find(m=>m.key===viewMonth)?.label}
          </div>
          <div style={{display:"flex",gap:16,fontSize:12}}>
            <span style={{color:"#ef4444"}}>مصاريف: {summary.totalMonthCost.toLocaleString()} ريال</span>
            <span style={{color:"#22c55e"}}>إيراد: {summary.totalMonthRev.toLocaleString()} ريال</span>
            <span style={{color:pc(summary.totalMonthPnL),fontWeight:800}}>
              {summary.totalMonthPnL>=0?"فائض: +":"عجز: "}{summary.totalMonthPnL.toLocaleString()} ريال
            </span>
          </div>
        </div>
        <div style={{background:"rgba(255,255,255,0.06)",borderRadius:20,height:16,overflow:"hidden",position:"relative"}}>
          <div style={{height:"100%",borderRadius:20,width:`${Math.min(100,summary.covPct)}%`,
            background:summary.covPct>=100?"linear-gradient(90deg,#22c55e,#16a34a)":summary.covPct>=80?"linear-gradient(90deg,#f59e0b,#d97706)":"linear-gradient(90deg,#ef4444,#dc2626)",
            transition:"width .5s ease",display:"flex",alignItems:"center",justifyContent:"flex-end",paddingLeft:8}}>
            {summary.covPct>20&&<span style={{fontSize:10,fontWeight:800,color:"#fff"}}>{summary.covPct}%</span>}
          </div>
          {summary.covPct<=20&&<span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:10,fontWeight:800,color:"#64748b"}}>{summary.covPct}%</span>}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:11,color:"#475569"}}>
          <span>٠٪ — لا إيراد</span>
          <span>نقطة التعادل: {summary.breakeven.toLocaleString()} ريال / شهر</span>
          <span>١٠٠٪ — تغطية كاملة</span>
        </div>
      </div>

      {/* ── Annual bar chart ── */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"16px 20px",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:14}}>{bl("مقارنة شهرية — الإيراد مقابل المصاريف الكاملة (إيجار + صيانة)","Monthly: Revenue vs Total Costs (Lease + Maintenance)")}</div>
        <div style={{display:"flex",gap:6,alignItems:"flex-end",overflowX:"auto",paddingBottom:4}}>
          {MONTHS.map(m=>{
            const rev   = contracts.reduce((s,c)=>s+getRevenue(c.id,m.key),0);
            const cost  = contracts.reduce((s,c)=>s+totalMonthlyCost(c.id,m.key),0);
            // Per-month breakeven for the bar chart — contracts active in THIS specific month only.
            const bmStart = m.key + '-01', bmEnd = m.key + '-31';
            const bmBreakeven = Math.round((contracts||[]).filter(c =>
              c.type !== 'storage' &&
              (!c.contractStart || c.contractStart <= bmEnd) &&
              (!c.contractEnd   || c.contractEnd   >= bmStart)
            ).reduce((s,c) => s + ((Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0)) / 12, 0));
            const maxV  = Math.max(rev,cost,bmBreakeven,1);
            const revH  = Math.round((rev/maxV)*110);
            const costH = Math.round((cost/maxV)*110);
            const beH   = Math.round((bmBreakeven/maxV)*110);
            const pnl   = rev-cost;
            const isCur = m.key===viewMonth;
            return(
              <div key={m.key} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,flex:1,minWidth:56,
                background:isCur?"rgba(200,160,60,0.05)":"transparent",borderRadius:8,padding:"4px 2px"}}>
                <div style={{position:"relative",display:"flex",gap:2,alignItems:"flex-end",height:115}}>
                  {/* Breakeven line marker */}
                  <div style={{position:"absolute",bottom:beH,left:-2,right:-2,borderTop:`1px dashed rgba(167,139,250,0.5)`,zIndex:3}}/>
                  <div title={`إيراد: ${rev.toLocaleString()}`}
                    style={{width:18,height:Math.max(revH,3),borderRadius:"3px 3px 0 0",background:pnl>=0?"rgba(34,197,94,0.75)":"rgba(34,197,94,0.35)",transition:"height .4s"}}/>
                  <div title={`مصاريف: ${cost.toLocaleString()}`}
                    style={{width:18,height:Math.max(costH,3),borderRadius:"3px 3px 0 0",background:"rgba(239,68,68,0.65)",transition:"height .4s"}}/>
                </div>
                <div style={{fontSize:10,color:isCur?"#c8a03c":"#475569",fontWeight:isCur?800:400}}>{m.short}</div>
                {pnl!==0&&rev>0&&<div style={{fontSize:9,color:pc(pnl),fontWeight:700}}>{pnl>0?"+":""}{pnl>999?Math.round(pnl/1000)+"k":pnl}</div>}
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:16,marginTop:8,flexWrap:"wrap"}}>
          {[["rgba(34,197,94,0.75)","إيراد"],["rgba(239,68,68,0.65)","مصاريف (إيجار+صيانة)"],["rgba(167,139,250,0.5)","نقطة التعادل -- -- --"]].map(([c,l])=>(
            <span key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748b"}}>
              <span style={{width:12,height:12,borderRadius:2,background:c,display:"inline-block"}}/>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* ── Occupancy / vacancy days report ── */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"16px 20px",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>📅 أيام الإشغال مقابل الشغور (من بداية السنة)</div>
        <div style={{fontSize:11,color:"#64748b",marginBottom:14}}>الأرقام الفعلية فقط — بدون توقعات. المعدل اليومي يتغير حسب العرض والطلب.</div>
        {(()=>{
          const todayISO = TODAY.toISOString().slice(0,10);
          const daysBetween = (a,b)=>{ const d1=new Date(a),d2=new Date(b); if(isNaN(d1)||isNaN(d2))return 0; return Math.max(0,Math.round((d2-d1)/(1000*60*60*24))+1); };
          const monthDays = mk => { const [y,m]=mk.split('-').map(Number); return new Date(y,m,0).getDate(); };
          // Include archived contracts that were active any time this year — they still had vacant/occupied days.
          const yearStartKey = MONTHS[0]?.key + '-01';
          const activeUnits = contracts.filter(c => c.type !== 'storage' && (Number(c.annualRent)||0) > 0
            && (!c.contractEnd || c.contractEnd >= yearStartKey));
          const rows = activeUnits.map(c=>{
            let occupied=0, revenue=0, totalPossible=0;
            const dailyRates = [];
            MONTHS.forEach(m=>{
              if(m.key > todayISO.slice(0,7)) return;
              // Only count days this contract was actually active in this month.
              const monStart = m.key + '-01';
              const monEnd   = m.key + '-' + String(monthDays(m.key)).padStart(2,'0');
              if (c.contractStart && c.contractStart > monEnd) return; // not started yet
              if (c.contractEnd   && c.contractEnd   < monStart) return; // already ended
              const effStart = (c.contractStart && c.contractStart > monStart) ? c.contractStart : monStart;
              const effEnd   = (c.contractEnd   && c.contractEnd   < monEnd)   ? c.contractEnd   : monEnd;
              totalPossible += daysBetween(effStart, effEnd);
              const op = monthly[`${c.id}-${m.key}`];
              if(!op) return;
              const tenants = Array.isArray(op.tenants) && op.tenants.length ? op.tenants : [];
              if(tenants.length){
                tenants.forEach(t=>{
                  revenue += Number(t.amount)||0;
                  if(t.from && t.to){
                    const d = daysBetween(t.from,t.to);
                    occupied += d;
                    if(d>0 && Number(t.amount)>0) dailyRates.push((Number(t.amount)||0)/d);
                  } else if((Number(t.amount)||0)>0) occupied += monthDays(m.key);
                });
              } else if(op.status==='مشغول' || (Number(op.rentCollected)||0)>0){
                occupied += monthDays(m.key);
                revenue  += Number(op.rentCollected)||0;
              }
            });
            const vacant = Math.max(0,totalPossible-occupied);
            const occPct = totalPossible>0 ? Math.round((occupied/totalPossible)*100) : 0;
            const minRate = dailyRates.length ? Math.round(Math.min(...dailyRates)) : 0;
            const maxRate = dailyRates.length ? Math.round(Math.max(...dailyRates)) : 0;
            const avgRate = occupied>0 ? Math.round(revenue/occupied) : 0;
            return { c, occupied, vacant, totalPossible, occPct, revenue, minRate, avgRate, maxRate, hasRateData: dailyRates.length>0 };
          }).sort((a,b)=>b.vacant-a.vacant);
          const totalVacantDays = rows.reduce((s,r)=>s+r.vacant,0);
          const totalRevenue = rows.reduce((s,r)=>s+r.revenue,0);
          return (
            <>
              <div style={{display:"grid",gridTemplateColumns:"1.5fr 0.9fr 0.9fr 0.9fr 1fr 1.5fr",gap:8,padding:"8px 10px",fontSize:11,color:"#475569",fontWeight:700,borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                <span>الوحدة</span><span style={{textAlign:"center"}}>مشغول (يوم)</span><span style={{textAlign:"center"}}>شاغر (يوم)</span><span style={{textAlign:"center"}}>الإشغال %</span><span style={{textAlign:"center"}}>الإيراد</span><span style={{textAlign:"center"}}>المعدل اليومي (أدنى / متوسط / أعلى)</span>
              </div>
              {rows.map(r=>(
                <div key={r.c.id} style={{display:"grid",gridTemplateColumns:"1.5fr 0.9fr 0.9fr 0.9fr 1fr 1.5fr",gap:8,padding:"9px 10px",fontSize:12,color:"#e2e8f0",borderBottom:"1px solid rgba(255,255,255,0.03)",alignItems:"center"}}>
                  <span style={{color:"#c8a03c",fontWeight:700}}>{r.c.unitNumber||r.c.unitName} <span style={{color:"#475569",fontWeight:400,marginRight:6}}>{r.c.district}</span></span>
                  <span style={{textAlign:"center",color:"#22c55e"}}>{r.occupied}</span>
                  <span style={{textAlign:"center",color:r.vacant>60?"#ef4444":r.vacant>30?"#f59e0b":"#64748b"}}>{r.vacant}</span>
                  <span style={{textAlign:"center",fontWeight:700,color:r.occPct>=70?"#22c55e":r.occPct>=40?"#f59e0b":"#ef4444"}}>{r.occPct}%</span>
                  <span style={{textAlign:"center",color:"#94a3b8"}}>{r.revenue.toLocaleString()} ر</span>
                  <span style={{textAlign:"center",color:"#94a3b8",fontSize:11}}>
                    {r.hasRateData
                      ? <>{r.minRate.toLocaleString()} / <b style={{color:"#e2e8f0"}}>{r.avgRate.toLocaleString()}</b> / {r.maxRate.toLocaleString()}</>
                      : r.avgRate ? <>— / <b style={{color:"#e2e8f0"}}>{r.avgRate.toLocaleString()}</b> / —</> : "—"}
                  </span>
                </div>
              ))}
              <div style={{marginTop:10,padding:"10px 14px",background:"rgba(200,160,60,0.07)",border:"1px solid rgba(200,160,60,0.2)",borderRadius:10,display:"flex",justifyContent:"space-between",fontSize:13}}>
                <span style={{color:"#94a3b8",fontWeight:700}}>إجمالي أيام الشغور / الإيراد</span>
                <span style={{color:"#c8a03c",fontWeight:800}}>{totalVacantDays} يوم شاغر · {totalRevenue.toLocaleString()} ر إيراد</span>
              </div>
            </>
          );
        })()}
      </div>

      {/* ── Unit-by-unit table ── */}
      <div style={{marginBottom:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"#475569"}}>ترتيب حسب:</span>
        {sBtn("pnl","الربحية")} {sBtn("rev","الإيراد")} {sBtn("cost","التكلفة")}
      </div>

      <div style={{display:"grid",gap:10}}>
        {sortedUnits.map(c=>{
          const lCost  = monthlyLeaseCost(c.id, viewMonth);
          const maint  = getMaintenanceCost(c.id,viewMonth);
          const tCost  = lCost + maint;
          const rev    = getRevenue(c.id,viewMonth);
          const pnl    = monthlyPnL(c.id,viewMonth);
          const aRev   = annualRevenue(c.id);
          const aCost  = annualTotalCost(c.id);
          const aPnL   = aRev - aCost;
          const be     = breakevenRate(c.id);
          const covPct = tCost>0&&rev>0 ? Math.round((rev/tCost)*100) : 0;
          // "No lease" now means the master contract has no annualRent recorded — fix in Contracts tab.
          const noLease= !contractAnnualCost(c.id);

          return(
            <div key={c.id} style={{background:noLease?"rgba(255,255,255,0.02)":pb(pnl),border:`1px solid ${noLease?"rgba(255,255,255,0.06)":pbd(pnl)}`,borderRadius:14,padding:"14px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>

                {/* Unit ID + Ejar (linked to master contract) */}
                <div style={{display:"flex",alignItems:"center",gap:12,minWidth:160}}>
                  <div>
                    <span style={{fontSize:20,fontWeight:800,color:"#c8a03c"}}>{c.unitNumber}</span>
                    <span style={{fontSize:12,color:"#64748b",marginRight:8}}>{c.district}</span>
                    {c.ejarNum && <div style={{fontSize:10,color:"#475569",marginTop:2,direction:"ltr",textAlign:"right"}}>Ejar {c.ejarNum}</div>}
                    {noLease&&<span style={{fontSize:10,color:"#f59e0b",background:"rgba(245,158,11,0.1)",padding:"2px 7px",borderRadius:6}}>لا يوجد تكلفة إيجار في العقد</span>}
                  </div>
                </div>

                {/* Cost breakdown */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                  <CostPill label="إيجار/شهر"  value={lCost}  color="#ef4444"/>
                  {maint>0&&<CostPill label="صيانة"      value={maint}  color="#f97316"/>}
                  <CostPill label="إجمالي تكلفة" value={tCost} color="#fbbf24" bold/>
                  <span style={{color:"#334155",fontSize:14}}>←</span>
                  <CostPill label="إيراد الشهر" value={rev}   color="#22c55e" bold/>
                </div>

                {/* P&L monthly */}
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:10,color:"#475569",marginBottom:3}}>ربح/خسارة الشهر</div>
                  <div style={{fontSize:20,fontWeight:800,color:pc(pnl)}}>{pnl>0?"+":""}{pnl.toLocaleString()} <span style={{fontSize:11,fontWeight:400}}>ريال</span></div>
                </div>

                {/* Coverage bar mini */}
                {!noLease&&tCost>0&&(
                  <div style={{minWidth:100}}>
                    <div style={{fontSize:10,color:"#475569",marginBottom:4,textAlign:"right"}}>تغطية التكاليف</div>
                    <div style={{background:"rgba(255,255,255,0.06)",borderRadius:8,height:8,overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:8,width:`${Math.min(100,covPct)}%`,background:covPct>=100?"#22c55e":covPct>=80?"#f59e0b":"#ef4444",transition:"width .4s"}}/>
                    </div>
                    <div style={{fontSize:10,color:pc(pnl),fontWeight:700,textAlign:"left",marginTop:2}}>{covPct}%</div>
                  </div>
                )}

                {/* Annual summary */}
                {!noLease&&(
                  <div style={{textAlign:"right",borderRight:"1px solid rgba(255,255,255,0.07)",paddingRight:12}}>
                    <div style={{fontSize:10,color:"#475569",marginBottom:3}}>الربح/الخسارة السنوي (مسجّل)</div>
                    <div style={{fontSize:14,fontWeight:700,color:pc(aPnL)}}>{aPnL>0?"+":""}{aPnL.toLocaleString()} ريال</div>
                    {be!==null&&<div style={{fontSize:10,color:"#64748b",marginTop:2}}>نقطة التعادل: {be}% من الإيجار</div>}
                  </div>
                )}

                {/* Edit button — only supplementary fields (otherMonthly, setupFee, assumedRent, notes) */}
                <button onClick={()=>openEdit(c)}
                  style={{padding:"5px 14px",borderRadius:8,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.22)",color:"#c8a03c",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Tajawal',sans-serif",alignSelf:"center"}}>
                  ✏️
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Annual summary row ── */}
      <div style={{background:"rgba(200,160,60,0.06)",border:"2px solid rgba(200,160,60,0.25)",borderRadius:14,padding:"16px 20px",marginTop:16,display:"flex",gap:24,flexWrap:"wrap",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:14,fontWeight:800,color:"#c8a03c"}}>📊 الملخص السنوي للمحفظة الكاملة</div>
        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:10,color:"#64748b"}}>إجمالي عقود الإيجار (سنوي)</div>
            <div style={{fontSize:16,fontWeight:800,color:"#ef4444"}}>{(summary.breakeven*12).toLocaleString()} ريال</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:10,color:"#64748b"}}>نقطة التعادل الشهرية (محسوبة)</div>
            <div style={{fontSize:16,fontWeight:800,color:"#a78bfa"}}>{summary.breakeven.toLocaleString()} ريال</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:10,color:"#64748b"}}>إجمالي الإيرادات المسجّلة</div>
            <div style={{fontSize:16,fontWeight:800,color:"#22c55e"}}>{summary.totalAnnualRev.toLocaleString()} ريال</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:10,color:"#64748b"}}>صافي سنوي (مسجّل حتى الآن)</div>
            <div style={{fontSize:16,fontWeight:800,color:pc(summary.totalAnnualPnL)}}>{summary.totalAnnualPnL>0?"+":""}{summary.totalAnnualPnL.toLocaleString()} ريال</div>
          </div>
        </div>
      </div>


      {/* Edit Modal — supplementary fields only. Primary lease terms live on the master contract. */}
      {editing && <LeaseEditModal
        editing={editing}
        form={form}
        setForm={setForm}
        contracts={contracts}
        onClose={()=>setEditing(null)}
        onSave={save}
        pc={pc}
      />}
    </div>
  );
}


function LeaseEditModal({ editing, form, setForm, contracts, onClose, onSave, pc }){
  // Primary lease terms are read from the master contract (mutable in the Contracts tab only).
  // Only supplementary fields are editable here: otherMonthly extras, setupFee, assumedRent, notes.
  const c = contracts.find(x => x.id === editing) || {};
  const contractAnnual    = (Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0);
  const monthlyFromContract = Math.round(contractAnnual / 12);
  const fullMonthly       = monthlyFromContract + (Number(form.otherMonthly)||0);
  const margin            = (Number(form.assumedRent)||0) - fullMonthly;
  const beMonths          = Number(form.assumedRent)>0 && contractAnnual>0
    ? (contractAnnual / Number(form.assumedRent)).toFixed(1)
    : null;
  return(
    <Modal title={`تكاليف وحدة ${c.unitNumber||"—"} — ${c.district||""}`} onClose={onClose} wide>
      {/* Read-only reference block sourced from the master contract */}
      <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:800,color:"#64748b",marginBottom:8}}>من عقد المالك (يُعدَّل في تبويب العقود الرئيسية)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
          <div><span style={{color:"#64748b"}}>رقم إيجار: </span><span style={{color:"#e2e8f0",fontWeight:700,direction:"ltr"}}>{c.ejarNum||"—"}</span></div>
          <div><span style={{color:"#64748b"}}>المدينة/الحي: </span><span style={{color:"#e2e8f0"}}>{c.city||"—"} · {c.district||"—"}</span></div>
          <div><span style={{color:"#64748b"}}>إيجار سنوي: </span><span style={{color:"#ef4444",fontWeight:700}}>{(Number(c.annualRent)||0).toLocaleString()} ر</span></div>
          <div><span style={{color:"#64748b"}}>رسوم مكتب: </span><span style={{color:"#e2e8f0"}}>{(Number(c.officeFee)||0).toLocaleString()} ر</span></div>
          <div><span style={{color:"#64748b"}}>مياه سنوي: </span><span style={{color:"#e2e8f0"}}>{(Number(c.waterAnnual)||0).toLocaleString()} ر</span></div>
          <div><span style={{color:"#64748b"}}>فترة العقد: </span><span style={{color:"#e2e8f0",direction:"ltr"}}>{c.contractStart||"—"} → {c.contractEnd||"—"}</span></div>
        </div>
        {contractAnnual>0 && (
          <div style={{marginTop:10,paddingTop:8,borderTop:"1px dashed rgba(255,255,255,0.06)",display:"flex",justifyContent:"space-between",fontSize:13}}>
            <span style={{color:"#94a3b8"}}>التكلفة الشهرية المشتقة من العقد</span>
            <span style={{color:"#ef4444",fontWeight:800}}>{monthlyFromContract.toLocaleString()} ريال</span>
          </div>
        )}
      </div>

      {/* Supplementary fields — Finance-tab only */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="مصاريف مكتب وتأسيس إضافية (ريال)" value={form.setupFee||""} onChange={v=>setForm(f=>({...f,setupFee:v}))} type="number"/>
        <Field label="مصاريف ثابتة شهرية أخرى (ريال)"  value={form.otherMonthly||""} onChange={v=>setForm(f=>({...f,otherMonthly:v}))} type="number"/>
        <div style={{gridColumn:"1/-1"}}>
          <Field label="الإيجار المستهدف من المستأجر الفرعي (ريال/شهر)" value={form.assumedRent||""} onChange={v=>setForm(f=>({...f,assumedRent:v}))} type="number"/>
        </div>
      </div>

      {contractAnnual>0 && Number(form.assumedRent)>0 && (
        <div style={{background:margin>=0?"rgba(34,197,94,0.07)":"rgba(239,68,68,0.07)",border:`1px solid ${margin>=0?"rgba(34,197,94,0.2)":"rgba(239,68,68,0.2)"}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:800,color:"#94a3b8",marginBottom:8}}>تحليل التعادل</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,fontSize:12}}>
            <div style={{textAlign:"right"}}><div style={{color:"#64748b",fontSize:10}}>التكلفة الشهرية</div><div style={{color:"#ef4444",fontWeight:800,fontSize:15}}>{fullMonthly.toLocaleString()} ر</div></div>
            <div style={{textAlign:"right"}}><div style={{color:"#64748b",fontSize:10}}>الإيراد المستهدف</div><div style={{color:"#22c55e",fontWeight:800,fontSize:15}}>{Number(form.assumedRent).toLocaleString()} ر</div></div>
            <div style={{textAlign:"right"}}><div style={{color:"#64748b",fontSize:10}}>هامش الربح الشهري</div><div style={{color:pc(margin),fontWeight:800,fontSize:15}}>{margin>0?"+":""}{margin.toLocaleString()} ر</div></div>
          </div>
          {beMonths && (
            <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:8,marginTop:8,fontSize:12}}>
              <span style={{color:"#64748b"}}>أشهر لتعادل التكلفة السنوية: </span>
              <span style={{color:"#c8a03c",fontWeight:800}}>{beMonths} شهر</span>
              <span style={{color:"#475569",fontSize:11}}> من 12 شهر</span>
            </div>
          )}
        </div>
      )}
      <Field label="ملاحظات" value={form.notes||""} onChange={v=>setForm(f=>({...f,notes:v}))} multiline rows={2}/>
      <SaveBtn onClick={onSave}/>
    </Modal>
  );
}


function BalanceSheetTab({ units, leases, monthly, tenants, contracts = [], invoices = [], schedule = [], overhead = [] }){
  const {S, lang} = useLang();
  const T = S.monthly;   // was missing — BalanceSheetTab referenced T and crashed
  const bl = (ar, en) => lang === "en" ? en : ar;
  const [cash, setCash]       = useState(SEED_CASH);
  const [editCash, setEditCash] = useState(false);
  const [cashForm, setCashForm] = useState({...SEED_CASH});
  const [asOf] = useState(CURRENT_MONTH_KEY);

  const month = MONTHS.find(m=>m.key===asOf)?.label || asOf;

  // ── Helpers ──
  // Contracts are the source of truth for the primary landlord-side lease terms
  // (annualRent + officeFee + waterAnnual). `leases[cid]` still holds supplementary
  // Finance-tab-only fields (otherMonthly extras, notes). Renewing a contract in the
  // Contracts tab therefore flows through here automatically for all future months.
  function getContract(cid){ return contracts.find(c => c.id === cid) || {}; }
  function getLease(cid){ return leases[cid]||{otherMonthly:0}; }
  function contractAnnualCost(cid){
    const c = getContract(cid);
    return (Number(c.annualRent)||0) + (Number(c.officeFee)||0) + (Number(c.waterAnnual)||0);
  }
  function getOp(cid,mKey){ return monthly[`${cid}-${mKey}`]||{}; }
  // Revenue: recognised in the month an invoice is marked paid (matched to the contract by
  // contractId, ejarNum, or unit+district for legacy invoices). Falls back to monthly.rentCollected.
  function getRevenue(cid,mKey){
    const c = getContract(cid);
    const cu = String(c.unitNumber || c.unitName || '').toLowerCase();
    const cd = String(c.district || '').toLowerCase();
    const invoiceRev = (invoices||[]).reduce((s, inv) => {
      if (!inv.paidAt || !String(inv.paidAt).startsWith(mKey)) return s;
      const matches =
        (inv.contractId && inv.contractId === cid) ||
        (inv.ejarNum && c.ejarNum && String(inv.ejarNum).trim() === String(c.ejarNum).trim()) ||
        (!inv.contractId && !inv.ejarNum &&
          String(inv.unit||'').toLowerCase() === cu &&
          (!inv.district || String(inv.district||'').toLowerCase() === cd));
      return matches ? s + (Number(inv.rent)||0) : s;
    }, 0);
    return invoiceRev > 0 ? invoiceRev : (Number(getOp(cid,mKey).rentCollected)||0);
  }
  function getElec(cid,mKey){ return Number(getOp(cid,mKey).electricityCost)||0; }
  function getOtherExpenses(cid,mKey){ return Number(getOp(cid,mKey).maintenanceCost)||0; }
  // Variable cost for a month = electricity (fixed slot) + other expenses (flexible list)
  function getMaint(cid,mKey){ return getElec(cid,mKey) + getOtherExpenses(cid,mKey); }
  // Historical accuracy: prefer the frozen leaseCostAtRecord snapshot when the month was
  // recorded before the contract was renewed with a new rent.
  function frozenAnnualCost(cid, mKey){
    const op = mKey ? (monthly[`${cid}-${mKey}`]||{}) : {};
    if (op.leaseCostAtRecord != null) return Number(op.leaseCostAtRecord)||0;
    return contractAnnualCost(cid);
  }
  function monthlyLeaseCost(cid, mKey){
    const l = getLease(cid);
    return Math.round(frozenAnnualCost(cid, mKey)/12) + (Number(l.otherMonthly)||0);
  }

  // Active unit contracts drive rent/revenue/lease calcs. Storage locations are excluded from
  // those since they have no landlord/rent, but they still contribute furniture to Fixed Assets.
  // Archived contracts that were active any time this year still contribute to YTD numbers.
  const _todayISO_IS = TODAY.toISOString().slice(0,10);
  const _yearStart   = MONTHS[0]?.key + '-01';
  const activeContracts = contracts.filter(c =>
    c.type !== 'storage' && (Number(c.annualRent)||0) > 0
    && (!c.contractEnd || c.contractEnd >= _yearStart) // was active at some point this fiscal year
  );
  const activeStorage   = contracts.filter(c => c.status !== 'archived' && c.type === 'storage');

  // ── INCOME STATEMENT (current month) ──
  const totalRevenue    = activeContracts.reduce((s,c)=>s+getRevenue(c.id,asOf),0);
  const totalLeaseCost  = activeContracts.reduce((s,c)=>s+monthlyLeaseCost(c.id, asOf),0);
  const totalMaintCost  = activeContracts.reduce((s,c)=>s+getMaint(c.id,asOf),0);
  const overheadCost    = overheadTotalForMonth(overhead, asOf);
  const grossProfit     = totalRevenue - totalLeaseCost;
  const operatingProfit = grossProfit - totalMaintCost - overheadCost;
  const mgmtFee        = Math.round(totalRevenue * 0.15);
  const netProfit      = operatingProfit;
  const grossMargin    = totalRevenue>0 ? Math.round((grossProfit/totalRevenue)*100) : 0;
  const netMargin      = totalRevenue>0 ? Math.round((netProfit/totalRevenue)*100) : 0;

  // ── YTD (all months Apr–current) ──
  // Sum per month so frozen snapshots on past months are respected instead of extrapolating current cost.
  const ytdRevenue   = MONTHS.reduce((s,m)=>s+activeContracts.reduce((ss,c)=>ss+getRevenue(c.id,m.key),0),0);
  const ytdLease     = MONTHS.reduce((s,m)=>s+activeContracts.reduce((ss,c)=>ss+monthlyLeaseCost(c.id, m.key),0),0);
  const ytdMaint     = MONTHS.reduce((s,m)=>s+activeContracts.reduce((ss,c)=>ss+getMaint(c.id,m.key),0),0);
  const ytdOverhead  = MONTHS.reduce((s,m)=>s+overheadTotalForMonth(overhead, m.key),0);
  const ytdNet       = ytdRevenue - ytdLease - ytdMaint - ytdOverhead;

  // ══════════════════════════════════════════════════════════════════
  // Rental Arbitrage Balance Sheet Model
  //
  // Ibrahim doesn't OWN the properties — he leases from landlords and sublets. So:
  //   • Assets = cash + real assets he owns (furniture, deposits with landlords) +
  //              money on the way in (invoices signed but unpaid)
  //   • Liabilities = money he actually owes (this month's landlord rent, tenant deposits
  //              held, future lease obligations from active contracts through year-end)
  //   • Equity = retained earnings from operations
  // The lease contract itself is NEVER an asset — it's a stream of obligations.
  // ══════════════════════════════════════════════════════════════════
  const todayStr = TODAY.toISOString().slice(0,10);

  // ── CURRENT ASSETS ──
  const totalCash = cash.current + cash.reserve + cash.operations;

  // Accounts receivable: invoices signed but not yet marked paid.
  const receivableInvoices = (invoices||[]).filter(inv => inv.signUrl && !inv.paidAt);
  const accountsReceivable = receivableInvoices.reduce((s,inv) => s + (Number(inv.total)||Number(inv.rent)||0), 0);

  // Real prepaid rent to landlords: cash paid AHEAD via the payments schedule.
  // If landlord rent for a coming month is already marked paid in schedule, that's prepaid.
  const prepaidRent = (schedule||[]).reduce((s, sch) => {
    if (!sch.paid || !sch.nextPaymentDate) return s;
    // Only count if the paid date is in a future month
    const paidMonth = String(sch.nextPaymentDate).slice(0,7);
    const currentMonth = todayStr.slice(0,7);
    return paidMonth > currentMonth ? s + (Number(sch.amount)||0) : s;
  }, 0);

  const totalCurrentAssets = totalCash + accountsReceivable + prepaidRent;

  // ── FIXED ASSETS ──
  // Furniture: sum of itemised purchases per contract (from the "🛋️ إضافة أثاث" flow)
  // plus any legacy `furnitureValue` starting balance. Split into two lines on the Balance
  // Sheet: furniture deployed in units, and furniture sitting in storage warehouses.
  const contractFurniture = c => {
    const items = Array.isArray(c?.furniturePurchases) ? c.furniturePurchases : [];
    return items.reduce((s,i)=>s+(Number(i.amount)||0),0) + (Number(c?.furnitureValue)||0);
  };
  const furnitureInUnits   = activeContracts.reduce((s,c)=>s+contractFurniture(c),0);
  const furnitureInStorage = activeStorage.reduce((s,c)=>s+contractFurniture(c),0);
  const furnitureAssets    = furnitureInUnits + furnitureInStorage;
  // Security deposits Ibrahim paid to landlords (recoverable at end of lease).
  const depositsAtLandlords = activeContracts.reduce((s,c)=>{
    if (c.depositReturned) return s;
    return s + (Number(c.depositAmount)||0);
  }, 0);
  const totalFixedAssets = furnitureAssets + depositsAtLandlords;

  const totalAssets = totalCurrentAssets + totalFixedAssets;

  // ── CURRENT LIABILITIES ──
  // This month's rent obligation to landlords (already accrued, not yet paid).
  const monthlyObligations = totalLeaseCost;
  const accruedLiabilities = monthlyObligations;

  // Tenant deposits held on behalf of active renters (owed back at end of tenancy).
  const activeInvoices = (invoices||[]).filter(inv => {
    if (!inv.contractStart || !inv.contractEnd) return false;
    const effectiveEnd = inv.vacatedAt || inv.contractEnd;
    return inv.contractStart <= todayStr && todayStr <= effectiveEnd;
  });
  const tenantDeposits = activeInvoices.reduce((s,inv) => s + (Number(inv.deposit)||0), 0);

  const totalCurrentLiabilities = accruedLiabilities + tenantDeposits;

  // ── LONG-TERM LIABILITIES ──
  // Future lease commitments: for each active contract, remaining months × monthly rent.
  const futureLeaseObligations = activeContracts.reduce((s,c) => {
    if (!c.contractEnd) return s;
    const end = new Date(c.contractEnd);
    const now = new Date(todayStr);
    if (end <= now) return s;
    const monthsLeft = Math.max(0, (end.getFullYear()-now.getFullYear())*12 + (end.getMonth()-now.getMonth()));
    return s + monthsLeft * (contractAnnualCost(c.id)/12);
  }, 0);
  const totalLongTermLiabilities = Math.round(futureLeaseObligations);

  const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;

  // ── EQUITY ──
  const retainedEarnings = ytdNet;
  const equity           = totalAssets - totalLiabilities;

  const Row = ({label,value,sub,indent,bold,color,border}) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:`${indent?"6px 6px 6px 22px":"8px 6px"}`,borderTop:border?"1px solid rgba(255,255,255,0.07)":"none",borderBottom:border==="bottom"?"2px solid rgba(200,160,60,0.3)":"none"}}>
      <div>
        <div style={{fontSize:bold?14:13,fontWeight:bold?700:400,color:color||"#e2e8f0"}}>{label}</div>
        {sub&&<div style={{fontSize:11,color:"#475569",marginTop:1}}>{sub}</div>}
      </div>
      <div style={{fontSize:bold?16:14,fontWeight:bold?800:500,color:color||(value>=0?"#e2e8f0":"#ef4444"),textAlign:"right",minWidth:120}}>
        {value!==undefined&&value!==null ? `${value<0?"-":""}${Math.abs(value).toLocaleString()} ريال` : ""}
      </div>
    </div>
  );

  const Section = ({title,color,children}) => (
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,overflow:"hidden",marginBottom:16}}>
      <div style={{background:color||"rgba(200,160,60,0.08)",padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
        <span style={{fontSize:13,fontWeight:800,color:"#c8a03c"}}>{title}</span>
      </div>
      <div style={{padding:"4px 10px"}}>{children}</div>
    </div>
  );

  const KPI = ({label,value,color,sub}) => (
    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"14px 16px",textAlign:"right",flex:1,minWidth:130}}>
      <div style={{fontSize:11,color:"#64748b",marginBottom:4}}>{label}</div>
      <div style={{fontSize:20,fontWeight:800,color:color||"#e2e8f0",lineHeight:1}}>{value}</div>
      {sub&&<div style={{fontSize:10,color:"#475569",marginTop:3}}>{sub}</div>}
    </div>
  );

  const pc = v=>v>=0?"#22c55e":"#ef4444";

  return(
    <div style={{padding:"24px 28px",maxWidth:1100,margin:"0 auto"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');`}</style>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontSize:21,fontWeight:800,color:"#c8a03c",margin:0}}>⚖️ {bl("الميزانية العمومية","Balance Sheet")}</h2>
          <p style={{fontSize:13,color:"#475569",margin:"5px 0 0"}}>{month} · FindRE Operations</p>
        </div>
        <button onClick={()=>{setCashForm({...cash});setEditCash(true);}}
          style={{padding:"8px 16px",borderRadius:10,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.25)",color:"#c8a03c",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
          ✏️ تحديث أرصدة الكاش
        </button>
      </div>

      {/* KPIs */}
      <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
        <KPI label="إجمالي الأصول"      value={totalAssets.toLocaleString()+" ريال"}        color="#c8a03c"/>
        <KPI label="إجمالي الالتزامات"  value={totalLiabilities.toLocaleString()+" ريال"}    color="#ef4444"/>
        <KPI label="صافي حقوق الملكية"  value={equity.toLocaleString()+" ريال"}              color={pc(equity)}/>
        <KPI label="إيراد الشهر"        value={totalRevenue.toLocaleString()+" ريال"}         color="#22c55e" sub="مسجّل"/>
        <KPI label="صافي ربح الشهر"     value={(netProfit>=0?"+":"")+netProfit.toLocaleString()+" ريال"} color={pc(netProfit)}/>
        <KPI label="هامش الربح الصافي"  value={netMargin+"%"}                                  color={pc(netMargin)}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>

        {/* ── ASSETS ── */}
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#94a3b8",marginBottom:12,paddingBottom:8,borderBottom:"2px solid rgba(200,160,60,0.2)"}}>{bl("الأصول","ASSETS")}</div>

          <Section title={bl("الأصول المتداولة","Current Assets")} color="rgba(34,197,94,0.08)">
            <Row label="الحساب الجاري" value={cash.current} sub="Current Account"/>
            <Row label="حساب الاحتياطي" value={cash.reserve} sub="Cash Reserve"/>
            <Row label="حساب التشغيل الشهري" value={cash.operations} sub="Monthly Operations"/>
            <Row label="إجمالي الكاش" value={totalCash} bold color="#22c55e" border/>
            <Row label="الذمم المدينة (فواتير غير مدفوعة)" value={accountsReceivable} sub={`Accounts Receivable · ${receivableInvoices.length} فاتورة`} indent/>
            <Row label="إيجار مدفوع مسبقاً للملاك" value={prepaidRent} sub="Prepaid Rent · دفعات مبكرة فعلية" indent/>
            <Row label="إجمالي الأصول المتداولة" value={totalCurrentAssets} bold color="#c8a03c" border="bottom"/>
          </Section>

          <Section title={bl("الأصول الثابتة","Fixed Assets")} color="rgba(56,189,248,0.06)">
            <Row label="🛋️ أثاث في الوحدات" value={furnitureInUnits} sub={`Furniture in Units · ${activeContracts.filter(c=>contractFurniture(c)>0).length} وحدة`} indent/>
            {(furnitureInStorage>0 || activeStorage.length>0) && (
              <Row label="🏬 أثاث في المستودعات" value={furnitureInStorage} sub={`Furniture in Storage · ${activeStorage.length} مستودع`} indent/>
            )}
            <Row label="🔒 ودائع تأمين لدى الملاك" value={depositsAtLandlords} sub="Security Deposits Paid · قابلة للاسترداد" indent/>
            <Row label="إجمالي الأصول الثابتة" value={totalFixedAssets} bold color="#38bdf8" border="bottom"/>
          </Section>

          <div style={{display:"flex",justifyContent:"space-between",background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.3)",borderRadius:12,padding:"12px 16px"}}>
            <span style={{fontSize:15,fontWeight:800,color:"#c8a03c"}}>إجمالي الأصول</span>
            <span style={{fontSize:18,fontWeight:800,color:"#c8a03c"}}>{totalAssets.toLocaleString()} ريال</span>
          </div>
        </div>

        {/* ── LIABILITIES + EQUITY ── */}
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#94a3b8",marginBottom:12,paddingBottom:8,borderBottom:"2px solid rgba(239,68,68,0.3)"}}>الالتزامات وحقوق الملكية</div>

          <Section title={bl("الالتزامات المتداولة","Current Liabilities")} color="rgba(239,68,68,0.07)">
            <Row label="التزامات الإيجار الشهرية" value={accruedLiabilities} sub="المدفوعات المستحقة للملاك هذا الشهر" indent/>
            <Row label="ودائع المستأجرين" value={tenantDeposits} sub={`${activeInvoices.length} مستأجر نشط`} indent/>
            <Row label="إجمالي الالتزامات المتداولة" value={totalCurrentLiabilities} bold color="#ef4444" border="bottom"/>
          </Section>

          <Section title={bl("الالتزامات طويلة الأجل","Long-term Liabilities")} color="rgba(249,115,22,0.07)">
            <Row label="التزامات الإيجار المستقبلية" value={totalLongTermLiabilities} sub="Future Lease Obligations · باقي شهور العقود × الإيجار الشهري" indent/>
            <Row label="إجمالي الالتزامات طويلة الأجل" value={totalLongTermLiabilities} bold color="#f97316" border="bottom"/>
          </Section>

          <Section title={bl("حقوق الملكية","Equity")} color="rgba(167,139,250,0.07)">
            <Row label="الأرباح المحتجزة (YTD)" value={retainedEarnings} sub="صافي الإيرادات المسجّلة أبريل–الآن" indent color={pc(retainedEarnings)}/>
            <Row label="صافي حقوق الملكية" value={equity} bold color={pc(equity)} border="bottom"/>
          </Section>

          <div style={{display:"flex",justifyContent:"space-between",background:equity>=0?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)",border:`1px solid ${equity>=0?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.3)"}`,borderRadius:12,padding:"12px 16px",marginBottom:20}}>
            <span style={{fontSize:15,fontWeight:800,color:pc(equity)}}>إجمالي الالتزامات + الملكية</span>
            <span style={{fontSize:18,fontWeight:800,color:pc(equity)}}>{(totalLiabilities+equity).toLocaleString()} ريال</span>
          </div>

          {/* ── P&L Summary ── */}
          <Section title={bl("ملخص قائمة الدخل","P&L Summary")} color="rgba(200,160,60,0.07)">
            <Row label="إجمالي الإيراد الشهري" value={totalRevenue} color="#22c55e"/>
            <Row label="(−) تكلفة عقود الإيجار" value={-totalLeaseCost} color="#ef4444" indent sub="Cost of Revenue"/>
            <Row label="إجمالي الربح" value={grossProfit} bold color={pc(grossProfit)} border sub={`هامش ${grossMargin}%`}/>
            <Row label="(−) المصاريف التشغيلية" value={-totalMaintCost} color="#f97316" indent sub="Electricity + Other Expenses"/>
            {overheadCost>0 && <Row label="(−) مصاريف إدارية (G&A)" value={-overheadCost} color="#a78bfa" indent sub="SaaS · Government · Legal · Marketing"/>}
            <Row label="صافي الربح / الخسارة" value={netProfit} bold color={pc(netProfit)} border="bottom" sub={`هامش صافي ${netMargin}%`}/>
            <div style={{height:8}}/>
            <Row label="📅 إيراد YTD الإجمالي" value={ytdRevenue} color="#22c55e" sub="أبريل → الآن"/>
            <Row label="📅 صافي YTD" value={ytdNet} bold color={pc(ytdNet)} sub="بعد الإيجار والصيانة"/>
          </Section>

          {/* Cash flow mini */}
          <div style={{background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.18)",borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#38bdf8",marginBottom:10}}>💧 {bl("التدفق النقدي","Cash Position")}</div>
            {[["الحساب الجاري","#22c55e",cash.current],["الاحتياطي","#a78bfa",cash.reserve],["التشغيل","#f59e0b",cash.operations]].map(([l,c,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{fontSize:12,color:"#94a3b8"}}>{l}</span>
                <div style={{flex:1,margin:"0 10px",background:"rgba(255,255,255,0.05)",borderRadius:6,height:6,overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:6,background:c,width:`${Math.min(100,(v/totalCash)*100)}%`}}/>
                </div>
                <span style={{fontSize:12,fontWeight:700,color:c,minWidth:80,textAlign:"left"}}>{v.toLocaleString()} ريال</span>
              </div>
            ))}
            <div style={{borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:8,marginTop:8,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:12,color:"#94a3b8"}}>إجمالي السيولة</span>
              <span style={{fontSize:14,fontWeight:800,color:"#c8a03c"}}>{totalCash.toLocaleString()} ريال</span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Cash Modal */}
      {editCash&&(
        <Modal title="تحديث أرصدة الكاش" onClose={()=>setEditCash(false)}>
          {[["الحساب الجاري (Current Account)","current"],["حساب الاحتياطي (Cash Reserve)","reserve"],["حساب التشغيل الشهري (Operations)","operations"]].map(([lbl,key])=>(
            <div key={key} style={{marginBottom:13}}>
              <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:5}}>{lbl}</label>
              <input type="number" value={cashForm[key]} onChange={e=>setCashForm(f=>({...f,[key]:Number(e.target.value)||0}))}
                style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,outline:"none",direction:"ltr",textAlign:"left",fontFamily:"'Tajawal',sans-serif"}}/>
            </div>
          ))}
          <div style={{background:"rgba(200,160,60,0.07)",border:"1px solid rgba(200,160,60,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between"}}>
            <span style={{fontSize:13,color:"#94a3b8"}}>{T.maintModalTotal}</span>
            <span style={{fontSize:15,fontWeight:800,color:"#c8a03c"}}>{(cashForm.current+cashForm.reserve+cashForm.operations).toLocaleString()} ريال</span>
          </div>
          <SaveBtn onClick={()=>{setCash({...cashForm});setEditCash(false);}}/>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// OVERHEAD TAB — General & Administrative expenses (SaaS, government, legal, etc.)
// ══════════════════════════════════════════════════════════════════
export function OverheadTab({ overhead = [], setOverhead }){
  const [editingId, setEditingId] = useState(null); // 'new' or an id
  const [form, setForm] = useState({});
  const [viewMonth, setViewMonth] = useState(CURRENT_MONTH_KEY);
  const [catFilter, setCatFilter] = useState('all');

  function openAdd(){
    // Default start date to the FIRST day of the selected view month, so a new entry lands in the month
    // the user is looking at. Prevents accidentally logging to today when viewing a past/future month.
    setForm({ category: 'saas', desc: '', amount: '', startDate: viewMonth + '-01', freq: 'once', endDate: '', notes: '' });
    setEditingId('new');
  }
  function openEdit(entry){
    setForm({
      category:  entry.category  || 'other',
      desc:      entry.desc      || '',
      amount:    String(entry.amount || ''),
      startDate: entry.startDate || TODAY.toISOString().slice(0,10),
      freq:      entry.freq      || 'monthly',
      endDate:   entry.endDate   || '',
      notes:     entry.notes     || '',
    });
    setEditingId(entry.id);
  }
  function save(){
    if (!form.desc || !form.amount || !form.startDate) return;
    const rec = {
      category:  form.category,
      desc:      form.desc,
      amount:    Number(form.amount) || 0,
      startDate: form.startDate,
      freq:      form.freq || 'once',
      endDate:   form.endDate || '',
      notes:     form.notes || '',
    };
    if (editingId === 'new') {
      setOverhead(p => [...p, { ...rec, id: Date.now() }]);
    } else {
      setOverhead(p => p.map(e => e.id === editingId ? { ...e, ...rec } : e));
    }
    setEditingId(null); setForm({});
  }
  function del(id){
    if (!window.confirm('حذف هذا المصروف؟')) return;
    setOverhead(p => p.filter(e => e.id !== id));
  }

  // Derived: entries that hit the selected month
  const monthHits = (overhead||[]).filter(e => overheadHitsMonth(e, viewMonth));
  const monthTotal = monthHits.reduce((s,e)=>s+(Number(e.amount)||0),0);
  // List view = ONLY entries that hit the selected month, filtered by category.
  // Shows the user exactly what counts toward the KPI, so no confusion between months.
  const filtered = monthHits.filter(e => catFilter === 'all' || e.category === catFilter);
  // Category totals for this month
  const catTotals = OVERHEAD_CATEGORIES.map(c => ({
    ...c,
    total: monthHits.filter(e => e.category === c.key).reduce((s,e)=>s+(Number(e.amount)||0),0)
  }));

  const catStyle = key => OVERHEAD_CATEGORIES.find(c=>c.key===key) || OVERHEAD_CATEGORIES[4];
  const freqLabel = key => (OVERHEAD_FREQ.find(f=>f.key===key)||OVERHEAD_FREQ[0]).label;

  return (
    <div style={{ padding:"24px 28px", maxWidth:1200, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:22, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:21, fontWeight:800, color:"#a78bfa", margin:0 }}>🏢 المصاريف الإدارية والعامة (G&A)</h2>
          <p style={{ fontSize:13, color:"#475569", margin:"5px 0 0" }}>SaaS · حكومي · قانوني · تسويق · مصاريف عامة غير مرتبطة بوحدة معينة</p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select value={viewMonth} onChange={e=>setViewMonth(e.target.value)}
            style={{ padding:"7px 12px", borderRadius:10, border:"1px solid rgba(255,255,255,0.1)", background:"#181b27", color:"#94a3b8", fontSize:13, outline:"none", fontFamily:"'Tajawal',sans-serif", direction:"rtl" }}>
            {MONTHS.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <button onClick={openAdd}
            style={{ padding:"8px 16px", borderRadius:10, background:"linear-gradient(135deg,#a78bfa,#7c3aed)", border:"none", color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Tajawal',sans-serif" }}>
            + إضافة مصروف
          </button>
        </div>
      </div>

      {/* Monthly total KPI + category breakdown */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10, marginBottom:22 }}>
        <div style={{ background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.2)", borderRadius:14, padding:"16px 18px" }}>
          <div style={{ fontSize:11, color:"#64748b", marginBottom:4 }}>إجمالي {MONTHS.find(m=>m.key===viewMonth)?.label}</div>
          <div style={{ fontSize:22, fontWeight:800, color:"#a78bfa" }}>{monthTotal.toLocaleString()} ريال</div>
          <div style={{ fontSize:10, color:"#475569", marginTop:3 }}>{monthHits.length} قيد نشط هذا الشهر</div>
        </div>
        {catTotals.filter(c=>c.total>0).map(c => (
          <div key={c.key} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"12px 14px" }}>
            <div style={{ fontSize:11, color:"#64748b", marginBottom:4 }}>{c.label}</div>
            <div style={{ fontSize:16, fontWeight:800, color:c.color }}>{c.total.toLocaleString()} ريال</div>
          </div>
        ))}
      </div>

      {/* Category filter chips */}
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
        {[{key:'all',label:'👁️ الكل',color:'#c8a03c'},...OVERHEAD_CATEGORIES].map(c => (
          <button key={c.key} onClick={()=>setCatFilter(c.key)}
            style={{ padding:"6px 12px", borderRadius:8, border:"1px solid", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"'Tajawal',sans-serif",
              background: catFilter===c.key ? `rgba(${c.key==='all'?'200,160,60':'167,139,250'},0.18)` : 'rgba(255,255,255,0.03)',
              color: catFilter===c.key ? (c.color||'#c8a03c') : '#64748b',
              borderColor: catFilter===c.key ? (c.color||'#c8a03c') : 'transparent' }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length===0 ? (
        <div style={{ textAlign:"center", color:"#334155", fontSize:14, padding:40, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:14 }}>
          لا مصاريف — اضغط "+ إضافة مصروف" للبدء
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
          {filtered.map(entry => {
            const cat = catStyle(entry.category);
            const hitsThisMonth = overheadHitsMonth(entry, viewMonth);
            return (
              <div key={entry.id} style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${hitsThisMonth?cat.color+'55':'rgba(255,255,255,0.07)'}`, borderRadius:14, padding:"14px 16px", opacity: hitsThisMonth ? 1 : 0.7 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11, color:cat.color, fontWeight:700, marginBottom:3 }}>{cat.label}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#e2e8f0" }}>{entry.desc}</div>
                  </div>
                  <div style={{ fontSize:16, fontWeight:800, color:cat.color, whiteSpace:"nowrap", marginRight:8 }}>{Number(entry.amount).toLocaleString()} ر</div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8, fontSize:11, color:"#64748b" }}>
                  <span style={{ background:"rgba(255,255,255,0.04)", padding:"2px 8px", borderRadius:5 }}>📅 {freqLabel(entry.freq)}</span>
                  <span>من {entry.startDate}</span>
                  {entry.endDate && <span>→ {entry.endDate}</span>}
                </div>
                {entry.notes && <div style={{ fontSize:11, color:"#475569", marginBottom:8 }}>{entry.notes}</div>}
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={()=>openEdit(entry)}
                    style={{ flex:1, padding:"5px 10px", borderRadius:7, background:"rgba(200,160,60,0.1)", border:"1px solid rgba(200,160,60,0.2)", color:"#c8a03c", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Tajawal',sans-serif" }}>
                    ✏️ تعديل
                  </button>
                  <button onClick={()=>del(entry.id)}
                    style={{ padding:"5px 10px", borderRadius:7, background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", color:"#ef4444", fontSize:11, cursor:"pointer" }}>
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit modal */}
      {editingId && (
        <Modal title={editingId==='new'?'إضافة مصروف إداري':'تعديل المصروف'} onClose={()=>{setEditingId(null);setForm({});}}>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:12, color:"#94a3b8", marginBottom:6 }}>الفئة</label>
            <select value={form.category||''} onChange={e=>setForm(f=>({...f,category:e.target.value}))}
              style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"10px 12px", color:"#e2e8f0", fontSize:14, outline:"none", direction:"rtl", fontFamily:"'Tajawal',sans-serif" }}>
              {OVERHEAD_CATEGORIES.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <Field label="الوصف" value={form.desc||''} onChange={v=>setForm(f=>({...f,desc:v}))} required/>
          <Field label="المبلغ (ريال)" value={form.amount||''} onChange={v=>setForm(f=>({...f,amount:v}))} type="number" required/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {(() => {
              // Lock the start-date picker to the selected view month to avoid logging to wrong month by mistake.
              const monthFirst = viewMonth + '-01';
              const [y,m] = viewMonth.split('-').map(Number);
              const lastDay = new Date(y, m, 0).getDate();
              const monthLast  = `${viewMonth}-${String(lastDay).padStart(2,'0')}`;
              return (
                <Field label={`تاريخ البداية (${MONTHS.find(mm=>mm.key===viewMonth)?.label})`} value={form.startDate||''}
                  onChange={v=>setForm(f=>({...f,startDate:v}))} type="date" required min={monthFirst} max={monthLast}/>
              );
            })()}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:"#94a3b8", marginBottom:6 }}>التكرار</label>
              <select value={form.freq||'once'} onChange={e=>setForm(f=>({...f,freq:e.target.value}))}
                style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"10px 12px", color:"#e2e8f0", fontSize:14, outline:"none", direction:"rtl", fontFamily:"'Tajawal',sans-serif" }}>
                {OVERHEAD_FREQ.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
          </div>
          <Field label="تاريخ التوقف (اختياري)" value={form.endDate||''} onChange={v=>setForm(f=>({...f,endDate:v}))} type="date"/>
          <Field label="ملاحظات" value={form.notes||''} onChange={v=>setForm(f=>({...f,notes:v}))}/>
          <SaveBtn onClick={save} disabled={!form.desc||!form.amount||!form.startDate}/>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// CASHFLOW TAB
// ══════════════════════════════════════════════════════════════════

function CashFlowTab({ units, leases, monthly, schedule, setSchedule, contracts = [], invoices = [], overhead = [] }){
  const {S, lang} = useLang();
  const T = S.ops;   // was missing — CashFlowTab referenced T and crashed
  const bl = (ar, en) => lang === "en" ? en : ar;
  const OPENING_CASH = 19723.23 + 5000 + 1500; // from balance sheet
  const MIN_CASH_THRESHOLD = 15000; // alert below this
  const [editUnit, setEditUnit]   = useState(null);
  const [editForm, setEditForm]   = useState({});

  // Contracts are the source of truth. `leases[cid].assumedRent` can still override the
  // expected inflow per unit; otherwise we fall back to contract.rent (what the tenant pays).
  // Recorded revenue = sum of invoices marked paid in that month (matched to contract),
  // falling back to monthly.rentCollected for legacy months.
  function getContract(cid){ return contracts.find(c => c.id === cid) || {}; }
  function getLease(cid){ return leases[cid]||{assumedRent:0}; }
  function getRecorded(cid, mKey){
    const c = getContract(cid);
    const cu = String(c.unitNumber || c.unitName || '').toLowerCase();
    const cd = String(c.district || '').toLowerCase();
    const invoiceRev = (invoices||[]).reduce((s, inv) => {
      if (!inv.paidAt || !String(inv.paidAt).startsWith(mKey)) return s;
      const matches =
        (inv.contractId && inv.contractId === cid) ||
        (inv.ejarNum && c.ejarNum && String(inv.ejarNum).trim() === String(c.ejarNum).trim()) ||
        (!inv.contractId && !inv.ejarNum &&
          String(inv.unit||'').toLowerCase() === cu &&
          (!inv.district || String(inv.district||'').toLowerCase() === cd));
      return matches ? s + (Number(inv.rent)||0) : s;
    }, 0);
    return invoiceRev > 0 ? invoiceRev : (Number((monthly[`${cid}-${mKey}`]||{}).rentCollected)||0);
  }
  function getExpected(cid){
    const l = getLease(cid);
    const c = getContract(cid);
    return Number(l.assumedRent) || Number(c.rent) || 0;
  }

  // ── Build monthly forecast Apr–Dec 2026 ──
  const forecast = useMemo(()=>{
    let runningBalance = OPENING_CASH;

    return MONTHS.map(m => {
      // Inflows: use recorded if available, else expected
      const inflow = contracts.reduce((s,c)=>{
        const rec = getRecorded(c.id, m.key);
        return s + (rec > 0 ? rec : getExpected(c.id));
      }, 0);

      // Outflows: landlord payments due THIS month. schedule[i].unitId is historically the
      // contract.id, so we look up the label from contracts first, with a units-array fallback
      // for any legacy schedule entries that predate the contract-as-source-of-truth switch.
      const outflowItems = [];
      // 1) Landlord payments due this month (fixed cost, from the payments schedule).
      schedule.forEach(s => {
        const allDates = generatePaymentDates(s.nextPaymentDate, s.freq);
        allDates.forEach(d => {
          if(d.startsWith(m.key)){
            const c = contracts.find(x=>x.id===s.unitId);
            const u = c ? null : (units||[]).find(x=>x.id===s.unitId);
            const label = c
              ? `${c.unitNumber || c.unitName || s.unitId} · ${c.district || ''}`
              : (u ? `${u.unitNumber} · ${u.district}` : s.unitId);
            outflowItems.push({
              unitId: s.unitId,
              unitLabel: label,
              amount: s.amount,
              category: 'landlord',
              freq: s.freq,
              date: d,
              paid: s.paid && d === s.nextPaymentDate,
            });
          }
        });
      });
      // 2) Operational expenses recorded in Monthly Ops (electricity + flexible other-expenses).
      //    These are actual cash outlays, so cash flow should reflect them just like landlord payments.
      contracts.forEach(c => {
        const op = monthly[`${c.id}-${m.key}`] || {};
        const elec = Number(op.electricityCost)||0;
        const other = Number(op.maintenanceCost)||0;
        const label = `${c.unitNumber || c.unitName || c.id} · ${c.district || ''}`;
        if (elec > 0) {
          outflowItems.push({ unitId: c.id, unitLabel: label, amount: elec, category: 'electricity', date: m.key, paid: true });
        }
        if (other > 0) {
          outflowItems.push({ unitId: c.id, unitLabel: label, amount: other, category: 'other', date: m.key, paid: true });
        }
      });
      // 3) G&A overhead entries that hit this month (SaaS, government, legal, etc.).
      (overhead||[]).forEach(entry => {
        if (!overheadHitsMonth(entry, m.key)) return;
        outflowItems.push({
          unitId: entry.id,
          unitLabel: entry.desc || 'مصروف إداري',
          amount: Number(entry.amount) || 0,
          category: 'overhead',
          date: m.key,
          paid: true,
        });
      });

      const outflow = outflowItems.reduce((s,o)=>s+o.amount, 0);
      const netCF   = inflow - outflow;
      const openBal = runningBalance;
      runningBalance = runningBalance + netCF;

      return { month: m, inflow, outflow, outflowItems, netCF, openBal, closeBal: runningBalance };
    });
  }, [schedule, contracts, invoices, units, leases, monthly]);

  // ── Max values for bar chart scaling ──
  const maxBar = Math.max(...forecast.map(f=>Math.max(f.inflow, f.outflow)), 1);
  const maxBal = Math.max(...forecast.map(f=>Math.abs(f.closeBal)), OPENING_CASH, 1);

  const pc = v=>v>=0?"#22c55e":"#ef4444";
  const pb = v=>v>=0?"rgba(34,197,94,0.09)":"rgba(239,68,68,0.09)";
  const pbd= v=>v>=0?"rgba(34,197,94,0.25)":"rgba(239,68,68,0.25)";

  function openEdit(s){
    setEditForm({...s});
    setEditUnit(s.unitId);
  }
  function saveEdit(){
    setSchedule(p=>p.map(s=>s.unitId===editForm.unitId?{...editForm,amount:Number(editForm.amount)||0}:s));
    setEditUnit(null);
  }
  function markPaid(uid){
    setSchedule(p=>p.map(s=>s.unitId===uid?{...s,paid:true}:s));
  }

  // ── Critical months: balance < threshold ──
  const criticalMonths = forecast.filter(f=>f.closeBal < MIN_CASH_THRESHOLD);
  const biggestOutflow = forecast.reduce((mx,f)=>f.outflow>mx.outflow?f:mx, forecast[0]);

  return(
    <div style={{padding:"24px 28px",maxWidth:1300,margin:"0 auto"}}>

      {/* Header */}
      <div style={{marginBottom:22}}>
        <h2 style={{fontSize:21,fontWeight:800,color:"#c8a03c",margin:0}}>💧 Cash Flow Statement — 2026</h2>
        <p style={{fontSize:13,color:"#475569",margin:"5px 0 0"}}>
          توقعات التدفق النقدي · مواعيد دفع الملاك · الرصيد الشهري المتوقع
        </p>
      </div>

      {/* ── Summary KPIs ── */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {[
          {l:"رصيد الافتتاح",           v:OPENING_CASH.toLocaleString()+" ريال",      c:"#c8a03c"},
          {l:"إجمالي المدفوعات للملاك", v:forecast.reduce((s,f)=>s+f.outflow,0).toLocaleString()+" ريال", c:"#ef4444"},
          {l:"إجمالي الإيرادات المتوقعة",v:forecast.reduce((s,f)=>s+f.inflow,0).toLocaleString()+" ريال",  c:"#22c55e"},
          {l:"الرصيد المتوقع ديسمبر",   v:forecast[forecast.length-1]?.closeBal.toLocaleString()+" ريال", c:pc(forecast[forecast.length-1]?.closeBal)},
          {l:"أكبر ضغط شهري",           v:biggestOutflow?.month?.short+" ("+biggestOutflow?.outflow.toLocaleString()+" ر)", c:"#f97316"},
          {l:"أشهر تحت حد الأمان",      v:criticalMonths.length+" أشهر", c:criticalMonths.length>0?"#ef4444":"#22c55e"},
        ].map((k,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"13px 16px",flex:1,minWidth:130,textAlign:"right"}}>
            <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>{k.l}</div>
            <div style={{fontSize:15,fontWeight:800,color:k.c,lineHeight:1}}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* ── Critical alerts ── */}
      {criticalMonths.length>0&&(
        <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:12,padding:"12px 18px",marginBottom:18,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:800,color:"#fca5a5"}}>⚠️ تحذير — رصيد تحت حد الأمان ({MIN_CASH_THRESHOLD.toLocaleString()} ريال)</span>
          {criticalMonths.map(f=>(
            <span key={f.month.key} style={{fontSize:12,color:"#ef4444",background:"rgba(239,68,68,0.1)",padding:"3px 12px",borderRadius:6,fontWeight:700}}>
              {f.month.label}: {Math.round(f.closeBal).toLocaleString()} ريال
            </span>
          ))}
        </div>
      )}

      {/* ── Dual chart: bars + balance line ── */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:16}}>{bl("توقع التدفق النقدي الشهري — الإيراد vs مدفوعات الملاك vs الرصيد","Monthly Cash Flow — Revenue vs Owner Payments vs Balance")}</div>

        {/* Bar chart */}
        <div style={{display:"flex",gap:6,alignItems:"flex-end",marginBottom:12,overflowX:"auto",paddingBottom:4}}>
          {forecast.map(f=>{
            const inH  = Math.round((f.inflow/maxBar)*100);
            const outH = Math.round((f.outflow/maxBar)*100);
            const balH = Math.round((Math.abs(f.closeBal)/maxBal)*100);
            const isCrit = f.closeBal < MIN_CASH_THRESHOLD;
            return(
              <div key={f.month.key} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,flex:1,minWidth:62,background:isCrit?"rgba(239,68,68,0.04)":"transparent",borderRadius:8,padding:"4px 3px"}}>
                <div style={{display:"flex",gap:2,alignItems:"flex-end",height:105,position:"relative",width:"100%",justifyContent:"center"}}>
                  {/* Threshold line */}
                  <div style={{position:"absolute",bottom:Math.round((MIN_CASH_THRESHOLD/maxBal)*100),left:0,right:0,borderTop:"1px dashed rgba(239,68,68,0.4)",zIndex:2}}/>
                  <div title={`إيراد: ${f.inflow.toLocaleString()}`} style={{width:16,height:Math.max(inH,2),borderRadius:"3px 3px 0 0",background:"rgba(34,197,94,0.75)"}}/>
                  <div title={`مدفوعات: ${f.outflow.toLocaleString()}`} style={{width:16,height:Math.max(outH,2),borderRadius:"3px 3px 0 0",background:"rgba(239,68,68,0.7)"}}/>
                  <div title={`رصيد: ${Math.round(f.closeBal).toLocaleString()}`} style={{width:10,height:Math.max(balH,2),borderRadius:"3px 3px 0 0",background:isCrit?"rgba(239,68,68,0.9)":"rgba(200,160,60,0.85)"}}/>
                </div>
                <div style={{fontSize:10,fontWeight:700,color:"#475569"}}>{f.month.short}</div>
                {f.outflow>0&&<div style={{fontSize:9,color:"#ef4444",fontWeight:700}}>-{f.outflow>999?Math.round(f.outflow/1000)+"k":f.outflow}</div>}
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          {[["rgba(34,197,94,0.75)","إيراد متوقع"],["rgba(239,68,68,0.7)","مدفوعات الملاك"],["rgba(200,160,60,0.85)","رصيد نهاية الشهر"],["rgba(239,68,68,0.4)","حد الأمان (-- --)"]].map(([c,l])=>(
            <span key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748b"}}>
              <span style={{width:12,height:12,borderRadius:2,background:c,display:"inline-block"}}/>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* ── Monthly Detail Table ── */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,overflow:"hidden",marginBottom:24}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"rgba(0,0,0,0.35)"}}>
              {["الشهر","رصيد الافتتاح","إيراد متوقع","مدفوعات الملاك","صافي التدفق","رصيد الإغلاق","تفاصيل المدفوعات"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"right",fontSize:10,color:"#475569",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {forecast.map(f=>{
              const isCrit = f.closeBal < MIN_CASH_THRESHOLD;
              const isHeavy = f.outflow > f.inflow;
              return(
                <tr key={f.month.key} style={{borderTop:"1px solid rgba(255,255,255,0.04)",background:isCrit?"rgba(239,68,68,0.05)":isHeavy?"rgba(245,158,11,0.03)":"transparent"}}>
                  <td style={{padding:"11px 14px"}}>
                    <span style={{fontSize:13,fontWeight:700,color:isCrit?"#ef4444":"#e2e8f0"}}>{f.month.label}</span>
                    {isCrit&&<span style={{fontSize:10,color:"#ef4444",display:"block",marginTop:2}}>⚠️ تحت الحد</span>}
                  </td>
                  <td style={{padding:"11px 14px",fontSize:13,color:"#94a3b8",whiteSpace:"nowrap"}}>{Math.round(f.openBal).toLocaleString()} ر</td>
                  <td style={{padding:"11px 14px",fontSize:13,fontWeight:600,color:"#22c55e",whiteSpace:"nowrap"}}>+{f.inflow.toLocaleString()} ر</td>
                  <td style={{padding:"11px 14px",fontSize:13,fontWeight:700,color:f.outflow>0?"#ef4444":"#334155",whiteSpace:"nowrap"}}>
                    {f.outflow>0?"-"+f.outflow.toLocaleString()+" ر":"—"}
                  </td>
                  <td style={{padding:"11px 14px",whiteSpace:"nowrap"}}>
                    <span style={{fontSize:13,fontWeight:800,color:pc(f.netCF)}}>{f.netCF>=0?"+":""}{Math.round(f.netCF).toLocaleString()} ر</span>
                  </td>
                  <td style={{padding:"11px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,background:pb(f.closeBal),border:`1px solid ${pbd(f.closeBal)}`,borderRadius:8,padding:"4px 10px",display:"inline-flex",whiteSpace:"nowrap"}}>
                      <span style={{fontSize:13,fontWeight:800,color:pc(f.closeBal)}}>{Math.round(f.closeBal).toLocaleString()} ر</span>
                    </div>
                  </td>
                  <td style={{padding:"11px 14px"}}>
                    {f.outflowItems.length===0
                      ? <span style={{fontSize:11,color:"#334155"}}>لا مدفوعات</span>
                      : <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          {f.outflowItems.map((o,i)=>{
                            // Color-code by category: landlord=red, electricity=amber, other=orange.
                            const cat = o.category || 'landlord';
                            const style = cat === 'electricity'
                              ? { bg:'rgba(251,191,36,0.1)', fg:'#fbbf24', bd:'rgba(251,191,36,0.25)', icon:'⚡' }
                              : cat === 'other'
                              ? { bg:'rgba(249,115,22,0.1)', fg:'#f97316', bd:'rgba(249,115,22,0.25)', icon:'💸' }
                              : cat === 'overhead'
                              ? { bg:'rgba(167,139,250,0.1)', fg:'#a78bfa', bd:'rgba(167,139,250,0.25)', icon:'🏢' }
                              : { bg:'rgba(239,68,68,0.1)', fg:'#ef4444', bd:'rgba(239,68,68,0.2)',  icon:'🏠' };
                            return (
                              <span key={i} title={cat} style={{fontSize:10,background:style.bg,color:style.fg,border:`1px solid ${style.bd}`,padding:"2px 7px",borderRadius:5,whiteSpace:"nowrap"}}>
                                {style.icon} {o.unitLabel} {o.amount.toLocaleString()} ر
                              </span>
                            );
                          })}
                        </div>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Master Contract Payments Log (actual paid/pending) ── */}
      {contracts && contracts.length > 0 && (() => {
        // Flatten all payment entries from all contracts
        const allEntries = [];
        contracts.forEach(c => {
          if (!c.paymentsLog) return;
          Object.entries(c.paymentsLog).forEach(([date, entry]) => {
            allEntries.push({
              date,
              paid: !!entry.paid,
              paidAt: entry.paidAt || null,
              amount: Number(entry.amount) || 0,
              unitNumber: c.unitNumber,
              district: c.district || '',
              contractId: c.id,
            });
          });
        });
        if (allEntries.length === 0) return null;
        allEntries.sort((a, b) => a.date < b.date ? -1 : 1);
        const totalPaid    = allEntries.filter(e => e.paid).reduce((s, e) => s + e.amount, 0);
        const totalPending = allEntries.filter(e => !e.paid).reduce((s, e) => s + e.amount, 0);
        const overdue      = allEntries.filter(e => !e.paid && e.date < new Date().toISOString().slice(0,10));
        return (
          <div style={{marginBottom:24}}>
            <div style={{fontSize:14,fontWeight:800,color:"#94a3b8",marginBottom:12,paddingBottom:8,borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
              📒 سجل مدفوعات عقود الإيجار الفعلية
            </div>
            {/* Summary pills */}
            <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
              <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:10,padding:"10px 16px",textAlign:"right"}}>
                <div style={{fontSize:10,color:"#64748b",marginBottom:3}}>إجمالي المدفوع</div>
                <div style={{fontSize:16,fontWeight:800,color:"#22c55e"}}>{totalPaid.toLocaleString()} ر</div>
              </div>
              <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,padding:"10px 16px",textAlign:"right"}}>
                <div style={{fontSize:10,color:"#64748b",marginBottom:3}}>إجمالي المعلق</div>
                <div style={{fontSize:16,fontWeight:800,color:"#ef4444"}}>{totalPending.toLocaleString()} ر</div>
              </div>
              {overdue.length > 0 && (
                <div style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.35)",borderRadius:10,padding:"10px 16px",textAlign:"right"}}>
                  <div style={{fontSize:10,color:"#64748b",marginBottom:3}}>متأخرة</div>
                  <div style={{fontSize:16,fontWeight:800,color:"#fca5a5"}}>{overdue.length} دفعة ⚠️</div>
                </div>
              )}
            </div>
            {/* Table */}
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:"rgba(0,0,0,0.35)"}}>
                    {["تاريخ الدفعة","الوحدة","المبلغ","الحالة","تاريخ التسديد"].map(h=>(
                      <th key={h} style={{padding:"9px 14px",textAlign:"right",fontSize:10,color:"#475569",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allEntries.map((e, i) => {
                    const isOverdue = !e.paid && e.date < new Date().toISOString().slice(0,10);
                    return (
                      <tr key={i} style={{borderTop:"1px solid rgba(255,255,255,0.04)",background:isOverdue?"rgba(239,68,68,0.05)":"transparent"}}>
                        <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:isOverdue?"#fca5a5":"#e2e8f0",whiteSpace:"nowrap"}}>
                          {e.date}{isOverdue&&<span style={{fontSize:10,color:"#ef4444",marginRight:6}}> ⚠️ متأخر</span>}
                        </td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{fontSize:13,fontWeight:700,color:"#c8a03c"}}>{e.unitNumber}</span>
                          {e.district&&<span style={{fontSize:11,color:"#64748b",marginRight:6}}> {e.district}</span>}
                        </td>
                        <td style={{padding:"10px 14px",fontSize:13,fontWeight:800,color:"#ef4444",whiteSpace:"nowrap"}}>
                          -{e.amount.toLocaleString()} ر
                        </td>
                        <td style={{padding:"10px 14px"}}>
                          {e.paid
                            ? <span style={{fontSize:11,fontWeight:700,color:"#22c55e",background:"rgba(34,197,94,0.1)",padding:"2px 9px",borderRadius:6,border:"1px solid rgba(34,197,94,0.2)"}}>✅ مدفوع</span>
                            : <span style={{fontSize:11,fontWeight:700,color:isOverdue?"#fca5a5":"#f59e0b",background:isOverdue?"rgba(239,68,68,0.1)":"rgba(245,158,11,0.1)",padding:"2px 9px",borderRadius:6,border:`1px solid ${isOverdue?"rgba(239,68,68,0.3)":"rgba(245,158,11,0.3)"}`}}>{isOverdue?"⚠️ متأخر":"⏳ معلق"}</span>
                          }
                        </td>
                        <td style={{padding:"10px 14px",fontSize:12,color:"#64748b",whiteSpace:"nowrap"}}>
                          {e.paidAt || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── Upcoming Landlord Payments (next 60 days) ── */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:14,fontWeight:800,color:"#94a3b8",marginBottom:12,paddingBottom:8,borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
          📋 جدول مدفوعات الملاك — كل وحدة وتاريخها القادم
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
          {schedule.map(s=>{
            // Prefer the master contract for unit label; fall back to the legacy units array
            // for any older schedule rows still tied to a unit id (not a contract id).
            const c = contracts.find(x=>x.id===s.unitId);
            const u = c ? { unitNumber: c.unitNumber || c.unitName, district: c.district } : (units||[]).find(x=>x.id===s.unitId);
            const nextDate = new Date(s.nextPaymentDate);
            const daysUntil = Math.ceil((nextDate - TODAY)/(1000*60*60*24));
            const urgent = daysUntil <= 30;
            const overdue= daysUntil < 0;
            const nextDates = generatePaymentDates(s.nextPaymentDate, s.freq).slice(0,3);
            return(
              <div key={s.unitId} style={{background:overdue?"rgba(239,68,68,0.08)":urgent?"rgba(245,158,11,0.07)":"rgba(255,255,255,0.02)",border:`1px solid ${overdue?"rgba(239,68,68,0.25)":urgent?"rgba(245,158,11,0.2)":"rgba(255,255,255,0.07)"}`,borderRadius:12,padding:"14px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <span style={{fontSize:16,fontWeight:800,color:"#c8a03c"}}>{u?.unitNumber||s.unitId}</span>
                    <span style={{fontSize:11,color:"#64748b",marginRight:8}}>{u?.district}</span>
                    <span style={{fontSize:10,background:"rgba(167,139,250,0.15)",color:"#a78bfa",padding:"2px 7px",borderRadius:5,marginRight:4}}>
                      {s.freq==="quarterly"?"ربع سنوي":"نصف سنوي"}
                    </span>
                  </div>
                  <span style={{fontSize:16,fontWeight:800,color:overdue?"#ef4444":urgent?"#f59e0b":"#e2e8f0"}}>
                    {s.amount.toLocaleString()} ر
                  </span>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {nextDates.map(d=>(
                    <span key={d} style={{fontSize:11,color:d===nextDates[0]?(overdue?"#ef4444":urgent?"#f59e0b":"#22c55e"):"#475569",background:d===nextDates[0]?"rgba(255,255,255,0.06)":"transparent",padding:"2px 7px",borderRadius:4,border:d===nextDates[0]?"1px solid rgba(255,255,255,0.1)":"none",fontWeight:d===nextDates[0]?700:400}}>
                      {d===nextDates[0]&&(overdue?"متأخر ":urgent?`${daysUntil}د `:"")}
                      {String(d).slice(0,10)}
                    </span>
                  ))}
                </div>
                {s.note&&<div style={{fontSize:11,color:"#475569",marginBottom:8}}>{s.note}</div>}
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>openEdit(s)}
                    style={{flex:1,padding:"5px",borderRadius:7,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.2)",color:"#c8a03c",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    {T.editBtn}
                  </button>
                  {!s.paid&&<button onClick={()=>markPaid(s.unitId)}
                    style={{padding:"5px 10px",borderRadius:7,background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.25)",color:"#22c55e",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    ✓ دُفع
                  </button>}
                  {s.paid&&<span style={{fontSize:11,color:"#22c55e",padding:"5px 8px"}}>✅ مدفوع</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Expiring Contracts — Renewal Obligations ── */}
      {contracts && (() => {
        const expiring = contracts.filter(c => {
          if (!c.contractEnd) return false;
          const days = Math.ceil((new Date(c.contractEnd) - TODAY) / (1000 * 60 * 60 * 24));
          return days >= 0 && days <= 90 && c.renewalPlan?.status !== 'not_renewing';
        });
        if (expiring.length === 0) return null;

        // Sum of first installments using each contract's OWN freq
        const totalFirstPayments = expiring.reduce((s, c) => {
          const rent = c.renewalPlan?.annualRent ? Number(c.renewalPlan.annualRent) : Number(c.annualRent) || 0;
          const freq = Number(c.freq) || 2;
          return s + Math.round(rent / freq);
        }, 0);
        const totalRenewalAnnual = expiring.reduce((s, c) => {
          return s + (c.renewalPlan?.annualRent ? Number(c.renewalPlan.annualRent) : Number(c.annualRent) || 0);
        }, 0);

        return (
          <div style={{marginBottom:24}}>
            <div style={{fontSize:14,fontWeight:800,color:"#f97316",marginBottom:12,paddingBottom:8,borderBottom:"1px solid rgba(249,115,22,0.2)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <span>🔄 تجديدات متوقعة خلال ٩٠ يوم</span>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:16,fontWeight:800,color:"#f97316"}}>{totalFirstPayments.toLocaleString()} ريال</div>
                <div style={{fontSize:10,color:"#64748b"}}>إجمالي الدفعة الأولى عند التجديد (بنفس دورة كل عقد)</div>
                <div style={{fontSize:10,color:"#475569"}}>{totalRenewalAnnual.toLocaleString()} ريال سنوياً</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
              {expiring.map(c => {
                const daysLeft   = Math.ceil((new Date(c.contractEnd) - TODAY) / (1000 * 60 * 60 * 24));
                const currentRent = Number(c.annualRent) || 0;
                const renewRent   = c.renewalPlan?.annualRent ? Number(c.renewalPlan.annualRent) : currentRent;
                const perPayment  = c.freq ? Math.round(renewRent / Number(c.freq)) : Math.round(renewRent / 2);
                const diff        = renewRent - currentRent;
                const rpStatus    = c.renewalPlan?.status;
                const statusColor = rpStatus === 'confirmed' ? '#22c55e' : '#f59e0b';
                const statusLabel = rpStatus === 'confirmed' ? '✅ مؤكد' : rpStatus ? '🤝 تحت التفاوض' : '⏳ لم يُخطط بعد';
                return (
                  <div key={c.id} style={{background:"rgba(249,115,22,0.05)",border:"1px solid rgba(249,115,22,0.2)",borderRadius:12,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <span style={{fontSize:16,fontWeight:800,color:"#c8a03c"}}>{c.unitNumber}</span>
                        {c.district && <span style={{fontSize:11,color:"#64748b",marginRight:6}}>{c.district}</span>}
                        <div style={{fontSize:10,color:statusColor,fontWeight:700,marginTop:2}}>{statusLabel}</div>
                      </div>
                      <div style={{textAlign:"left"}}>
                        <div style={{fontSize:10,color:"#64748b"}}>ينتهي بعد</div>
                        <div style={{fontSize:16,fontWeight:800,color:daysLeft<=30?"#ef4444":"#f97316"}}>{daysLeft} يوم</div>
                        <div style={{fontSize:10,color:"#64748b"}}>{c.contractEnd}</div>
                      </div>
                    </div>
                    {/* First renewal payment */}
                    <div style={{background:"rgba(0,0,0,0.2)",borderRadius:8,padding:"10px 12px",marginBottom:8}}>
                      <div style={{fontSize:10,color:"#64748b",marginBottom:4,fontWeight:700}}>الدفعة الأولى عند التجديد</div>
                      <div style={{fontSize:20,fontWeight:800,color:"#f97316"}}>{perPayment.toLocaleString()} ريال</div>
                      <div style={{fontSize:10,color:"#64748b",marginTop:2}}>
                        {renewRent.toLocaleString()} ريال/سنة · {c.freq || 2} دفعات/سنة
                        {" · كل "}{Math.round(12 / (Number(c.freq) || 2))} شهر
                      </div>
                    </div>
                    {/* Change vs current */}
                    {diff !== 0 ? (
                      <div style={{fontSize:11,fontWeight:700,color:diff>0?"#ef4444":"#22c55e"}}>
                        {diff>0 ? `↑ زيادة ${diff.toLocaleString()} ريال/سنة` : `↓ تخفيض ${Math.abs(diff).toLocaleString()} ريال/سنة`}
                      </div>
                    ) : renewRent > 0 ? (
                      <div style={{fontSize:11,color:"#22c55e"}}>✓ نفس السعر الحالي</div>
                    ) : (
                      <div style={{fontSize:11,color:"#64748b"}}>سيُحدد عند التفاوض</div>
                    )}
                    {c.renewalPlan?.notes && (
                      <div style={{fontSize:11,color:"#64748b",marginTop:6,fontStyle:"italic",borderTop:"1px solid rgba(255,255,255,0.05)",paddingTop:6}}>{c.renewalPlan.notes}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Edit Modal */}
      {editUnit&&(
        <Modal title={`تعديل جدول الدفع — ${editForm.unitId}`} onClose={()=>setEditUnit(null)}>
          <div style={{marginBottom:13}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6}}>تكرار الدفع</label>
            <div style={{display:"flex",gap:8}}>
              {[["quarterly","ربع سنوي (كل ٣ أشهر)"],["semi-annual","نصف سنوي (كل ٦ أشهر)"]].map(([v,l])=>(
                <button key={v} onClick={()=>setEditForm(f=>({...f,freq:v}))}
                  style={{flex:1,padding:"8px",borderRadius:9,border:"1px solid",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                    background:editForm.freq===v?"rgba(167,139,250,0.15)":"rgba(255,255,255,0.03)",
                    color:editForm.freq===v?"#a78bfa":"#64748b",
                    borderColor:editForm.freq===v?"rgba(167,139,250,0.4)":"rgba(255,255,255,0.07)"}}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div style={{marginBottom:13}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6}}>مبلغ كل دفعة (ريال) *</label>
            <input type="number" value={editForm.amount||""} onChange={e=>setEditForm(f=>({...f,amount:e.target.value}))}
              style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,outline:"none",direction:"ltr",textAlign:"left",fontFamily:"'Tajawal',sans-serif"}}/>
            {editForm.amount>0&&(
              <div style={{fontSize:11,color:"#64748b",marginTop:5}}>
                يعادل {Math.round(editForm.amount/( editForm.freq==="quarterly"?3:6)).toLocaleString()} ريال/شهر
                {" · "} سنوياً: {Math.round(editForm.amount*(editForm.freq==="quarterly"?4:2)).toLocaleString()} ريال
              </div>
            )}
          </div>

          <div style={{marginBottom:13}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6}}>تاريخ الدفعة القادمة *</label>
            <input type="date" value={editForm.nextPaymentDate||""} onChange={e=>setEditForm(f=>({...f,nextPaymentDate:e.target.value}))}
              style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:14,outline:"none",direction:"ltr",fontFamily:"'Tajawal',sans-serif"}}/>
          </div>

          {editForm.nextPaymentDate&&(
            <div style={{background:"rgba(167,139,250,0.07)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
              <div style={{fontSize:11,color:"#94a3b8",marginBottom:6,fontWeight:700}}>مواعيد الدفع المتوقعة لباقي ٢٠٢٦</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {generatePaymentDates(editForm.nextPaymentDate, editForm.freq).map(d=>(
                  <span key={d} style={{fontSize:11,color:"#a78bfa",background:"rgba(167,139,250,0.1)",padding:"3px 9px",borderRadius:5}}>
                    {String(d).slice(0,10)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6}}>ملاحظات</label>
            <input value={editForm.note||""} onChange={e=>setEditForm(f=>({...f,note:e.target.value}))}
              style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,outline:"none",direction:"rtl",fontFamily:"'Tajawal',sans-serif"}}/>
          </div>

          <SaveBtn onClick={saveEdit} disabled={!editForm.amount||!editForm.nextPaymentDate}/>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SEED DATA — Master Contracts & Ops Config
// ══════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════════
// FINANCE & ACCOUNTING TAB (combined)
// ══════════════════════════════════════════════════════════════════

export default function FinanceTab({ units, leases, setLeases, monthly, tenants, schedule, setSchedule, contracts, setContracts = ()=>{}, invoices = [], overhead = [], setOverhead = ()=>{} }){
  const {S, lang, dir, font} = useLang();
  const [sub, setSub] = useState("income");
  const SUB = [
    { key:"income",   label:S.finance.income   },
    { key:"balance",  label:S.finance.balance  },
    { key:"cashflow", label:S.finance.cashflow },
  ];

  return(
    <div style={{minHeight:"100vh",background:"#0d0f1a"}}>
      {/* Sub-nav */}
      <div style={{background:"rgba(0,0,0,0.2)",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"12px 20px",display:"flex",gap:8,overflowX:"auto"}}>
        <span style={{fontSize:14,fontWeight:800,color:"#c8a03c",marginLeft:12,flexShrink:0,alignSelf:"center"}}>💰 Finance & Accounting</span>
        {SUB.map(s=>(
          <button key={s.key} onClick={()=>setSub(s.key)}
            style={{padding:"7px 16px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'Tajawal',sans-serif",flexShrink:0,
              background:sub===s.key?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.03)",
              color:sub===s.key?"#c8a03c":"#64748b",
              borderColor:sub===s.key?"rgba(200,160,60,0.4)":"rgba(255,255,255,0.07)"}}>
            {s.label}
          </button>
        ))}
      </div>

      {sub==="income"   && <ProfitabilityTab contracts={contracts} setContracts={setContracts} invoices={invoices} overhead={overhead} leases={leases} setLeases={setLeases} monthly={monthly}/>}
      {sub==="balance"  && <BalanceSheetTab  units={units} leases={leases} monthly={monthly} tenants={tenants} contracts={contracts} invoices={invoices} schedule={schedule} overhead={overhead}/>}
      {sub==="cashflow" && <CashFlowTab      units={units} leases={leases} monthly={monthly} schedule={schedule} setSchedule={setSchedule} contracts={contracts} invoices={invoices} overhead={overhead}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MASTER CONTRACTS TAB
// ══════════════════════════════════════════════════════════════════
